import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Shield, Plus, Check, X, FileText, Users, BookOpen, Layers, FolderOpen,
  Database, TrendingUp, Trash2, Edit, RefreshCw, Save, 
  Search, Eye, EyeOff, ChevronRight, ChevronLeft, Zap, Loader, AlertCircle,
  Calendar, Award, Timer, Target, Hash, Type, Info, Upload, Image, FileJson, Download, File, Copy,
  PanelLeftClose, PanelLeft, Globe, BarChart3, CheckCircle, ExternalLink
} from 'lucide-react';

import { compressImage } from '../lib/compressImage';
import { toast } from 'sonner';
import { getExamInfo } from './ExamDetails';
import { renderLatex } from '../lib/renderLatex';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data
  const [stats, setStats] = useState({});
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '' });
  const [staffLoading, setStaffLoading] = useState(false);
  
  // Selection state
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  
  // Edit mode
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Add mode
  const [addingType, setAddingType] = useState(null);
  const [addForm, setAddForm] = useState({});
  
  // Expanded view for questions
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // Bulk import state
  const [bulkImportData, setBulkImportData] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importProgress, setImportProgress] = useState({ uploading: false, current: 0, total: 0 });
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // AI Parser state
  const [aiParserInput, setAiParserInput] = useState('');
  const [aiParsedQuestions, setAiParsedQuestions] = useState([]);
  const [aiProcessing, setAiProcessing] = useState(false);
  
  // PDF Upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ status: '', page: 0, total: 0 });
  const [pdfExtractedQuestions, setPdfExtractedQuestions] = useState([]);
  const [autoGenerateSolutions, setAutoGenerateSolutions] = useState(true);
  
  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  
  // Image Questions state
  const [imageQuestions, setImageQuestions] = useState([]);
  const [currentImageQuestion, setCurrentImageQuestion] = useState({
    question_image: null,
    question_text: '', // Optional text alongside image
    options: [
      { label: 'A', text: '', image: null },
      { label: 'B', text: '', image: null },
      { label: 'C', text: '', image: null },
      { label: 'D', text: '', image: null }
    ],
    correct_answer: 'A',
    explanation: '',
    solution_image: null,
    year: '',
    is_pyq: true,
    is_important: false,
    question_type: 'MCQ'
  });
  
  // Scraper state
  const [scraperUrl, setScraperUrl] = useState('');
  const [scraperLoading, setScraperLoading] = useState(false);
  const [scrapedQuestions, setScrapedQuestions] = useState([]);
  const [fetchBatchProgress, setFetchBatchProgress] = useState({ fetched: 0, total: 0, inProgress: false });
  
  // Bulk Scraper state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSubjectUrl, setBulkSubjectUrl] = useState('');
  const [availableChapters, setAvailableChapters] = useState([]);
  const [selectedBulkChapters, setSelectedBulkChapters] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, results: [] });
  const [chapterMappings, setChapterMappings] = useState({}); // { chapterSlug: localChapterId }

  // Question Manager improvements - pagination, filters, bulk actions
  const [questionsPage, setQuestionsPage] = useState(1);
  const [questionsTotalPages, setQuestionsTotalPages] = useState(1);
  const [questionsTotal, setQuestionsTotal] = useState(0);
  const [questionsPageSize] = useState(30);
  const [questionsSearch, setQuestionsSearch] = useState('');
  const [questionsDifficulty, setQuestionsDifficulty] = useState('');
  const [questionsType, setQuestionsType] = useState('');
  const [questionsYear, setQuestionsYear] = useState('');
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [inlineEditingId, setInlineEditingId] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState({});

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    loadStats();
    loadExams();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data || {});
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const loadExams = async () => {
    try {
      const res = await api.get('/exams');
      setExams(res.data || []);
    } catch (error) {
      toast.error('Failed to load exams');
    }
  };

  const loadSubjects = async (examId) => {
    try {
      // Don't show loading for fast operations
      const res = await api.get(`/exams/${examId}/subjects`);
      setSubjects(res.data || []);
      setSelectedExam(examId);
      setSelectedSubject(null);
      setChapters([]);
    } catch (error) {
      toast.error('Failed to load subjects');
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      // Don't show loading for fast operations
      const res = await api.get(`/subjects/${subjectId}/chapters`);
      setChapters(res.data || []);
      setSelectedSubject(subjectId);
    } catch (error) {
      toast.error('Failed to load chapters');
    }
  };

  const loadQuestions = async (filters = {}) => {
    try {
      setLoading(true);
      const params = { 
        limit: questionsPageSize,
        offset: (questionsPage - 1) * questionsPageSize
      };
      if (filters.exam_id) params.exam_id = filters.exam_id;
      if (filters.subject_id) params.subject_id = filters.subject_id;
      if (filters.chapter_id) params.chapter_id = filters.chapter_id;
      if (questionsSearch) params.search = questionsSearch;
      if (questionsDifficulty) params.difficulty = questionsDifficulty;
      if (questionsType) params.question_type = questionsType;
      if (questionsYear) params.year = questionsYear;
      
      const res = await api.get('/admin/questions', { params });
      const data = res.data;
      
      // Handle both old format (array) and new format (object with total)
      if (Array.isArray(data)) {
        setQuestions(data);
        setQuestionsTotal(data.length);
        setQuestionsTotalPages(1);
      } else {
        setQuestions(data.questions || []);
        setQuestionsTotal(data.total || 0);
        setQuestionsTotalPages(Math.ceil((data.total || 0) / questionsPageSize));
      }
    } catch (error) {
      toast.error('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      setStaffLoading(true);
      const res = await api.get('/admin/staff');
      setStaff(res.data || []);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setStaffLoading(false);
    }
  };

  const createStaff = async () => {
    if (!staffForm.name || !staffForm.email || !staffForm.password) {
      toast.error('Name, email and password are required');
      return;
    }
    try {
      setStaffLoading(true);
      await api.post('/admin/staff', { ...staffForm, role: 'content_editor' });
      toast.success('Staff account created');
      setStaffForm({ name: '', email: '', password: '' });
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff account');
    } finally {
      setStaffLoading(false);
    }
  };

  const deleteStaff = async (staffId, email) => {
    if (!window.confirm(`Remove staff member ${email}?`)) return;
    try {
      await api.delete(`/admin/staff/${staffId}`);
      toast.success('Staff member removed');
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff member');
    }
  };

  // ==================== QUICK TOGGLE AVAILABILITY ====================
  
  const toggleExamAvailability = async (exam) => {
    try {
      const newStatus = !exam.is_available;
      await api.put(`/admin/exams/${exam.id}`, { is_available: newStatus });
      toast.success(`${exam.name} is now ${newStatus ? 'AVAILABLE' : 'Coming Soon'}`);
      loadExams();
      loadStats();
    } catch (error) {
      toast.error('Failed to toggle availability: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ==================== CRUD OPERATIONS ====================

  const handleEdit = (type, item) => {
    setEditingItem({ type, id: item.id });
    // Deep copy for editing
    const formData = JSON.parse(JSON.stringify(item));
    
    // For questions, extract individual options
    if (type === 'questions' && item.options) {
      const opts = item.options;
      formData.optionA = opts.find(o => o.label === 'A')?.text || '';
      formData.optionB = opts.find(o => o.label === 'B')?.text || '';
      formData.optionC = opts.find(o => o.label === 'C')?.text || '';
      formData.optionD = opts.find(o => o.label === 'D')?.text || '';
    }

    // For exams, ensure exam_info is an object — pre-fill from static config if empty
    if (type === 'exams') {
      if (!formData.exam_info || Object.keys(formData.exam_info).length === 0) {
        // Try to get static data from the hardcoded exam career info
        const staticInfo = getExamInfo(item.id, item.name);
        if (staticInfo) {
          formData.exam_info = {
            fullName: staticInfo.fullName || '',
            conductedBy: staticInfo.conductedBy || '',
            totalCandidates: staticInfo.totalCandidates || '',
            examFrequency: staticInfo.examFrequency || '',
            overview: staticInfo.overview || '',
            eligibility: staticInfo.eligibility || {},
            examPattern: staticInfo.examPattern || {},
            importantDates2026: staticInfo.importantDates2026 || {},
            whyPrepare: staticInfo.whyPrepare || [],
            jobOpportunities: staticInfo.jobOpportunities || [],
            topColleges: staticInfo.topColleges || [],
            preparationTips: staticInfo.preparationTips || [],
            collegePackages: staticInfo.collegePackages || [],
            syllabus: staticInfo.syllabus || {},
          };
        } else {
          formData.exam_info = {};
        }
      }
    }
    
    setEditForm(formData);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    try {
      setLoading(true);
      const { type, id } = editingItem;
      
      let dataToSend = { ...editForm };
      
      // For questions, reconstruct options array
      if (type === 'questions') {
        dataToSend.options = [
          { label: 'A', text: editForm.optionA || '' },
          { label: 'B', text: editForm.optionB || '' },
          { label: 'C', text: editForm.optionC || '' },
          { label: 'D', text: editForm.optionD || '' },
        ];
        delete dataToSend.optionA;
        delete dataToSend.optionB;
        delete dataToSend.optionC;
        delete dataToSend.optionD;
      }
      
      await api.put(`/admin/${type}/${id}`, dataToSend);
      
      toast.success(`Updated successfully!`);
      setEditingItem(null);
      setEditForm({});
      
      // Reload data
      if (type === 'exams') loadExams();
      else if (type === 'subjects') loadSubjects(selectedExam);
      else if (type === 'chapters') loadChapters(selectedSubject);
      else if (type === 'questions') loadQuestions({ exam_id: selectedExam });
      else if (type === 'users') loadUsers();
      
      loadStats();
    } catch (error) {
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) return;
    
    try {
      setLoading(true);
      await api.delete(`/admin/${type}/${id}`);
      toast.success(`Deleted successfully!`);
      
      // Reload data
      if (type === 'exams') loadExams();
      else if (type === 'subjects') loadSubjects(selectedExam);
      else if (type === 'chapters') loadChapters(selectedSubject);
      else if (type === 'questions') loadQuestions({ exam_id: selectedExam });
      else if (type === 'users') loadUsers();
      
      loadStats();
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addingType) return;
    
    // Create optimistic item with temporary ID
    const tempId = `temp-${Date.now()}`;
    const optimisticItem = { ...addForm, id: tempId };
    
    try {
      // Optimistic update - add immediately to UI
      if (addingType === 'exams') {
        setExams(prev => [optimisticItem, ...prev]);
      } else if (addingType === 'subjects') {
        setSubjects(prev => [{ ...optimisticItem, exam_id: selectedExam }, ...prev]);
      } else if (addingType === 'chapters') {
        setChapters(prev => [{ ...optimisticItem, subject_id: selectedSubject }, ...prev]);
      }
      
      setAddingType(null);
      setAddForm({});
      toast.success('Creating...', { duration: 1000 });
      
      // Make actual API call in background
      let response;
      if (addingType === 'exams') {
        response = await api.post('/exams', addForm);
      } else if (addingType === 'subjects') {
        response = await api.post('/admin/subjects', { ...addForm, exam_id: selectedExam });
      } else if (addingType === 'chapters') {
        response = await api.post('/admin/chapters', { ...addForm, subject_id: selectedSubject });
      } else if (addingType === 'questions') {
        await api.post('/admin/questions', {
          ...addForm,
          exam_id: selectedExam,
          subject_id: selectedSubject || addForm.subject_id,
          chapter_id: selectedChapter || addForm.chapter_id,
          options: [
            { label: 'A', text: addForm.optionA || '' },
            { label: 'B', text: addForm.optionB || '' },
            { label: 'C', text: addForm.optionC || '' },
            { label: 'D', text: addForm.optionD || '' },
          ]
        });
      }
      
      // Replace temp item with real data
      if (response?.data) {
        if (addingType === 'exams') {
          setExams(prev => prev.map(item => item.id === tempId ? response.data : item));
        } else if (addingType === 'subjects') {
          setSubjects(prev => prev.map(item => item.id === tempId ? response.data : item));
        } else if (addingType === 'chapters') {
          setChapters(prev => prev.map(item => item.id === tempId ? response.data : item));
        }
      }
      
      toast.success(`Created successfully!`);
      
      // Reload only non-optimistic data
      if (addingType === 'questions') loadQuestions({ exam_id: selectedExam });
      loadStats();
    } catch (error) {
      // Rollback optimistic update on error
      if (addingType === 'exams') {
        setExams(prev => prev.filter(item => item.id !== tempId));
      } else if (addingType === 'subjects') {
        setSubjects(prev => prev.filter(item => item.id !== tempId));
      } else if (addingType === 'chapters') {
        setChapters(prev => prev.filter(item => item.id !== tempId));
      }
      toast.error('Failed to create: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const cancelAdd = () => {
    setAddingType(null);
    setAddForm({});
  };

  // ==================== BULK IMPORT FUNCTIONS ====================

  const handleJsonParse = () => {
    try {
      const parsed = JSON.parse(bulkImportData);
      const questions = Array.isArray(parsed) ? parsed : [parsed];
      setImportPreview(questions);
      toast.success(`Parsed ${questions.length} questions`);
    } catch (error) {
      toast.error('Invalid JSON: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name.endsWith('.json')) {
      const text = await file.text();
      setBulkImportData(text);
      try {
        const parsed = JSON.parse(text);
        const questions = Array.isArray(parsed) ? parsed : [parsed];
        setImportPreview(questions);
        toast.success(`Loaded ${questions.length} questions from file`);
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    } else {
      toast.error('Please upload a JSON file');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setImportProgress({ uploading: true, current: 0, total: files.length });

    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        // Compress image before upload
        const compressedFile = await compressImage(file);
        const formData = new FormData();
        formData.append('file', compressedFile);
        formData.append('category', 'questions');

        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push({
          original: file.name,
          url: res.data.url,
          filename: res.data.filename
        });
        setImportProgress(prev => ({ ...prev, current: i + 1 }));
      } catch (error) {
        console.error('Failed to upload:', file.name);
      }
    }

    setUploadedImages(prev => [...prev, ...uploaded]);
    setImportProgress({ uploading: false, current: 0, total: 0 });
    toast.success(`Uploaded ${uploaded.length} images`);
  };

  const handleBulkImport = async () => {
    if (importPreview.length === 0) {
      toast.error('No questions to import');
      return;
    }
    if (!selectedExam) {
      toast.error('Please select an exam first');
      return;
    }

    setLoading(true);
    try {
      // Add exam_id to all questions if not present
      const questionsWithExam = importPreview.map(q => ({
        ...q,
        exam_id: q.exam_id || selectedExam,
        subject_id: q.subject_id || selectedSubject || 'general',
        chapter_id: q.chapter_id || selectedChapter || 'general'
      }));

      const res = await api.post('/admin/questions/bulk', questionsWithExam);
      toast.success(`Imported ${res.data.count} questions!`);
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} questions had errors`);
      }

      // Reset
      setBulkImportData('');
      setImportPreview([]);
      loadStats();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        "question_text": "What is the SI unit of force?",
        "options": [
          { "label": "A", "text": "Newton" },
          { "label": "B", "text": "Joule" },
          { "label": "C", "text": "Watt" },
          { "label": "D", "text": "Pascal" }
        ],
        "correct_answer": "A",
        "explanation": "Force = mass × acceleration. The SI unit is Newton (N).",
        "difficulty": "easy",
        "year": 2023,
        "question_type": "MCQ",
        "tags": ["physics", "mechanics"],
        "is_pyq": true,
        "is_important": true,
        "source": "JEE Main 2023",
        "image_url": null,
        "option_images": null,
        "solution_image": null
      }
    ];

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question_template.json';
    a.click();
  };

  // ==================== AI PARSER FUNCTIONS ====================

  const parseQuestionWithAI = async (text) => {
    try {
      setAiProcessing(true);
      const res = await api.post('/admin/ai/parse-question-text', { 
        text, 
        subject: subjects.find(s => s.id === selectedSubject)?.name || 'Physics' 
      });
      return res.data;
    } catch (error) {
      toast.error('Failed to parse: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setAiProcessing(false);
    }
  };

  const generateSolutionForQuestion = async (question) => {
    try {
      setAiProcessing(true);
      const res = await api.post('/admin/ai/generate-solution', {
        question_text: question.question_text,
        options: question.options,
        subject: subjects.find(s => s.id === selectedSubject)?.name || 'Physics',
        diagram_description: question.diagram_description
      });
      return res.data;
    } catch (error) {
      toast.error('Failed to generate solution: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setAiProcessing(false);
    }
  };

  const handleParseMultipleQuestions = async () => {
    if (!aiParserInput.trim()) {
      toast.error('Please enter question text');
      return;
    }

    // Split by common separators (Q1, Q2, Q., 1., etc.)
    // eslint-disable-next-line no-useless-escape
    const questionBlocks = aiParserInput.split(/(?=Q\d+[\.\)]|^\d+[\.\)]|\n\n(?=\d+[\.\)]))/gm)
      .filter(block => block.trim().length > 20);

    if (questionBlocks.length === 0) {
      // Try to parse as single question
      const parsed = await parseQuestionWithAI(aiParserInput);
      if (parsed) {
        setAiParsedQuestions([parsed]);
        toast.success('Parsed 1 question');
      }
      return;
    }

    const parsed = [];
    for (let i = 0; i < questionBlocks.length; i++) {
      toast.info(`Parsing question ${i + 1}/${questionBlocks.length}...`);
      const result = await parseQuestionWithAI(questionBlocks[i]);
      if (result) {
        parsed.push(result);
      }
      // Small delay between requests
      await new Promise(r => setTimeout(r, 500));
    }

    setAiParsedQuestions(parsed);
    toast.success(`Parsed ${parsed.length} questions`);
  };

  const handleGenerateAllSolutions = async () => {
    if (aiParsedQuestions.length === 0) {
      toast.error('No questions to process');
      return;
    }

    const updated = [...aiParsedQuestions];
    for (let i = 0; i < updated.length; i++) {
      toast.info(`Generating solution ${i + 1}/${updated.length}...`);
      const solution = await generateSolutionForQuestion(updated[i]);
      if (solution) {
        updated[i] = {
          ...updated[i],
          explanation: solution.explanation,
          correct_answer: solution.correct_answer || updated[i].correct_answer
        };
      }
      await new Promise(r => setTimeout(r, 500));
    }

    setAiParsedQuestions(updated);
    toast.success('Generated all solutions!');
  };

  const handleImportParsedQuestions = async () => {
    if (aiParsedQuestions.length === 0) {
      toast.error('No questions to import');
      return;
    }
    if (!selectedExam) {
      toast.error('Please select an exam');
      return;
    }

    const questionsToImport = aiParsedQuestions.map(q => ({
      ...q,
      exam_id: selectedExam,
      subject_id: selectedSubject || 'general',
      chapter_id: selectedChapter || 'general',
      is_pyq: true,
      question_type: q.question_type || 'MCQ'
    }));

    try {
      setLoading(true);
      const res = await api.post('/admin/questions/bulk', questionsToImport);
      toast.success(`Imported ${res.data.count} questions!`);
      setAiParsedQuestions([]);
      setAiParserInput('');
      loadStats();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Question Manager: Bulk Actions  
  const handleBulkDeleteQuestions = async () => {
    if (selectedQuestionIds.length === 0) {
      toast.error('No questions selected');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedQuestionIds.length} selected questions?`)) return;
    
    try {
      setLoading(true);
      await api.post('/admin/questions/bulk-delete', { question_ids: selectedQuestionIds });
      toast.success(`Deleted ${selectedQuestionIds.length} questions`);
      setSelectedQuestionIds([]);
      loadQuestions({ exam_id: selectedExam, subject_id: selectedSubject });
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateQuestions = async (updates) => {
    if (selectedQuestionIds.length === 0) {
      toast.error('No questions selected');
      return;
    }
    
    try {
      setLoading(true);
      await api.post('/admin/questions/bulk-update', {
        question_ids: selectedQuestionIds,
        updates
      });
      toast.success(`Updated ${selectedQuestionIds.length} questions`);
      loadQuestions({ exam_id: selectedExam, subject_id: selectedSubject });
    } catch (error) {
      toast.error('Bulk update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInlineEdit = (question) => {
    setInlineEditingId(question.id);
    setInlineEditForm({
      question_text: question.question_text,
      correct_answer: question.correct_answer,
      difficulty: question.difficulty,
      year: question.year,
      options: question.options || []
    });
  };

  const handleSaveInlineEdit = async () => {
    try {
      setLoading(true);
      await api.put(`/admin/questions/${inlineEditingId}`, inlineEditForm);
      toast.success('Question updated');
      setInlineEditingId(null);
      loadQuestions({ exam_id: selectedExam, subject_id: selectedSubject });
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectQuestion = (id) => {
    setSelectedQuestionIds(prev => 
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const toggleSelectAllQuestions = () => {
    if (selectedQuestionIds.length === questions.length) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(questions.map(q => q.id));
    }
  };

  // Reload questions when filters or page changes
  useEffect(() => {
    if (activeTab === 'questions' && selectedExam) {
      loadQuestions({ exam_id: selectedExam, subject_id: selectedSubject });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsPage, questionsSearch, questionsDifficulty, questionsType, questionsYear]);

  // Sidebar navigation config (Admin-only - all sections always visible)
  const sidebarSections = [
    {
      label: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: Database, color: 'text-blue-500' },
      ]
    },
    {
      label: 'Content Management',
      items: [
        { id: 'exams', label: 'Exams', icon: BookOpen, color: 'text-blue-500' },
        { id: 'subjects', label: 'Subjects', icon: Layers, color: 'text-green-500' },
        { id: 'chapters', label: 'Chapters', icon: FolderOpen, color: 'text-yellow-500' },
        { id: 'questions', label: 'Questions', icon: FileText, color: 'text-purple-500' },
      ]
    },
    {
      label: 'Import Tools',
      items: [
        { id: 'scraper', label: 'Web Scraper', icon: Globe, color: 'text-cyan-500' },
        { id: 'pdf-upload', label: 'PDF / CSV Import', icon: File, color: 'text-red-500' },
        { id: 'bulk-import', label: 'Bulk Import', icon: Upload, color: 'text-indigo-500' },
        { id: 'ai-parser', label: 'AI Parser', icon: Zap, color: 'text-amber-500' },
      ]
    },
    {
      label: 'Administration',
      items: [
        { id: 'users', label: 'Users', icon: Users, color: 'text-cyan-500' },
        { id: 'staff', label: 'Staff', icon: Shield, color: 'text-red-400' },
      ]
    },
  ];

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId === 'users') loadUsers();
    if (tabId === 'staff') loadStaff();
    if (tabId === 'questions' && selectedExam) loadQuestions({ exam_id: selectedExam });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam]);

  if (user?.role !== 'admin') return null;

  const filteredExams = exams.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ==================== FIELD RENDERERS ====================

  const renderField = (label, field, form, setFormFn, type = 'text', options = null, icon = null) => {
    const Icon = icon;
    return (
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          {Icon && <Icon size={14} className="text-muted-foreground" />}
          {label}
        </label>
        {options ? (
          <select
            value={form[field] || ''}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.value })}
            className="w-full p-3 rounded-lg bg-background border border-border text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all"
          >
            <option value="">Select {label}</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={form[field] || ''}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.value })}
            className="w-full p-3 rounded-lg bg-background border border-border text-sm min-h-[100px] focus:ring-2 focus:ring-primary focus:border-primary transition-all"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        ) : type === 'checkbox' ? (
          <input
            type="checkbox"
            checked={form[field] || false}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.checked })}
            className="w-5 h-5 rounded border-border"
          />
        ) : (
          <Input
            type={type}
            value={form[field] ?? ''}
            onChange={(e) => setFormFn({ ...form, [field]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })}
            className="text-sm"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        )}
      </div>
    );
  };

  // Category options
  const categoryOptions = [
    { value: 'engineering', label: '🔧 Engineering' },
    { value: 'medical', label: '🏥 Medical' },
    { value: 'management', label: '📊 Management' },
    { value: 'government', label: '🏛️ Government' },
    { value: 'banking', label: '🏦 Banking' },
    { value: 'defence', label: '🎖️ Defence' },
    { value: 'law', label: '⚖️ Law' },
    { value: 'science', label: '🔬 Science' },
    { value: 'design', label: '🎨 Design' },
  ];

  const levelOptions = [
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'postgraduate', label: 'Postgraduate' },
    { value: 'professional', label: 'Professional' },
    { value: 'school', label: 'School Level' },
  ];

  const difficultyOptions = [
    { value: 'easy', label: '🟢 Easy' },
    { value: 'medium', label: '🟡 Medium' },
    { value: 'hard', label: '🔴 Hard' },
  ];

  const roleOptions = [
    { value: 'student', label: 'Student' },
    { value: 'content_editor', label: 'Content Editor' },
    { value: 'admin', label: 'Admin' },
  ];

  const answerOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
  ];

  return (
    <Layout>
      <div className="flex min-h-[calc(100vh-80px)]">

        {/* ====== SIDEBAR ====== */}
        <aside className={`${sidebarCollapsed ? 'w-16' : 'w-60'} flex-shrink-0 border-r border-border bg-card/50 transition-all duration-200 hidden md:flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-red-500" />
                <span className="font-bold text-sm text-foreground">Admin Panel</span>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>

          {/* Sidebar Navigation */}
          <nav className="flex-1 overflow-y-auto py-2">
            {sidebarSections.map((section) => (
              <div key={section.label} className="mb-1">
                {!sidebarCollapsed && (
                  <p className="px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {section.label}
                  </p>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                        isActive
                          ? 'bg-primary/10 text-primary border-r-2 border-primary font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <Icon size={18} className={isActive ? 'text-primary' : item.color} />
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Sidebar Stats (compact) */}
          {!sidebarCollapsed && (
            <div className="p-3 border-t border-border space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Exams</span>
                <span className="font-semibold text-foreground">{stats.total_exams || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Questions</span>
                <span className="font-semibold text-foreground">{stats.total_questions || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Users</span>
                <span className="font-semibold text-foreground">{stats.total_users || 0}</span>
              </div>
            </div>
          )}
        </aside>

        {/* ====== MOBILE TABS (shown on small screens only) ====== */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border overflow-x-auto">
          <div className="flex px-2 py-2 gap-1">
            {sidebarSections.flatMap(s => s.items).map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[10px] whitespace-nowrap transition-colors ${
                    activeTab === item.id ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <Icon size={16} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ====== MAIN CONTENT AREA ====== */}
        <main className="flex-1 overflow-y-auto">
          {/* Top Bar */}
          <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-sm border-b border-border px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold text-foreground capitalize">
                  {activeTab === 'pdf-upload' ? 'PDF / CSV Import' :
                   activeTab === 'bulk-import' ? 'Bulk Import' :
                   activeTab === 'ai-parser' ? 'AI Parser' :
                   activeTab === 'staff' ? 'Staff Management' : activeTab}
                </h1>
                {selectedExam && activeTab !== 'dashboard' && activeTab !== 'users' && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ChevronRight size={14} />
                    <span className="text-primary">{exams.find(e => e.id === selectedExam)?.name}</span>
                    {selectedSubject && (
                      <><ChevronRight size={14} /><span>{subjects.find(s => s.id === selectedSubject)?.name}</span></>
                    )}
                    {selectedChapter && (
                      <><ChevronRight size={14} /><span>{chapters.find(c => c.id === selectedChapter)?.name}</span></>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={loadStats} title="Refresh stats">
                  <RefreshCw size={14} />
                </Button>
                <Badge variant="outline" className="text-xs border-red-500/50 text-red-400">
                  Admin
                </Badge>
                <Badge variant="outline" className="text-xs">{user?.email}</Badge>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="p-6 pb-20 md:pb-6">

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              {[
                { icon: BookOpen, label: 'Exams', value: stats.total_exams || 0, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                { icon: Layers, label: 'Subjects', value: stats.total_subjects || 0, color: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/20' },
                { icon: FolderOpen, label: 'Chapters', value: stats.total_chapters || 0, color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
                { icon: FileText, label: 'Questions', value: stats.total_questions || 0, color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
                { icon: Users, label: 'Users', value: stats.total_users || 0, color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
                { icon: BarChart3, label: 'Tests Taken', value: stats.total_tests || 0, color: 'text-pink-500', bg: 'bg-pink-500/10', border: 'border-pink-500/20' },
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <Card key={idx} className={`p-4 rounded-xl border ${stat.border} ${stat.bg} hover:scale-[1.02] transition-transform`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={stat.color} size={16} />
                      <span className="text-xs text-muted-foreground">{stat.label}</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stat.value.toLocaleString()}</p>
                  </Card>
                );
              })}
            </div>

            {/* Quick Actions + Question Manager */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-5 bg-gradient-to-br from-primary/5 to-purple-500/5 border border-primary/20">
                <h3 className="font-bold text-foreground mb-3 flex items-center gap-2 text-sm">
                  <FileText size={18} className="text-primary" />
                  Question Manager
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Full-featured question editor with search, filters, and bulk actions.
                </p>
                <Button onClick={() => navigate('/admin/questions')} className="w-full bg-primary gap-2" size="sm">
                  <FileText size={16} /> Open Question Manager
                </Button>
              </Card>

              <Card className="p-5 border border-border">
                <h3 className="font-bold text-foreground mb-3 flex items-center gap-2 text-sm">
                  <Zap size={18} className="text-amber-500" />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" onClick={() => { handleTabChange('exams'); setAddingType('exams'); setAddForm({}); }} className="bg-blue-500 hover:bg-blue-600 text-xs">
                    <Plus size={14} className="mr-1" /> Add Exam
                  </Button>
                  <Button size="sm" onClick={() => handleTabChange('subjects')} className="bg-green-500 hover:bg-green-600 text-xs">
                    <Layers size={14} className="mr-1" /> Subjects
                  </Button>
                  <Button size="sm" onClick={() => { handleTabChange('users'); }} className="bg-cyan-500 hover:bg-cyan-600 text-xs">
                    <Users size={14} className="mr-1" /> Users
                  </Button>
                </div>
              </Card>
            </div>
            
            {/* Workflow Guide */}
            <Card className="p-5 border border-border">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2 text-sm">
                <Info size={18} className="text-blue-500" />
                Content Workflow
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
                {[
                  { step: '1', label: 'Create Exam', desc: 'Name, category, marks', color: 'blue' },
                  { step: '2', label: 'Add Subjects', desc: 'Physics, Chemistry...', color: 'green' },
                  { step: '3', label: 'Add Chapters', desc: 'Kinematics, Optics...', color: 'yellow' },
                  { step: '4', label: 'Import Questions', desc: 'Scrape, PDF, or manual', color: 'purple' },
                  { step: '5', label: 'Review & Publish', desc: 'Check quality', color: 'cyan' },
                ].map((s) => (
                  <div key={s.step} className={`p-3 rounded-lg bg-${s.color}-500/10 border border-${s.color}-500/20 text-center`}>
                    <div className={`w-6 h-6 rounded-full bg-${s.color}-500 text-white text-xs font-bold flex items-center justify-center mx-auto mb-1.5`}>{s.step}</div>
                    <p className="font-semibold text-foreground">{s.label}</p>
                    <p className="text-muted-foreground mt-0.5">{s.desc}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Current Selection */}
            {(selectedExam || selectedSubject || selectedChapter) && (
              <Card className="p-4 border border-border">
                <h4 className="font-medium text-foreground text-sm mb-2">Active Selection</h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {selectedExam && <Badge variant="outline"><BookOpen size={12} className="mr-1" />{exams.find(e => e.id === selectedExam)?.name}</Badge>}
                  {selectedSubject && <><ChevronRight size={14} /><Badge variant="outline"><Layers size={12} className="mr-1" />{subjects.find(s => s.id === selectedSubject)?.name}</Badge></>}
                  {selectedChapter && <><ChevronRight size={14} /><Badge variant="outline"><FolderOpen size={12} className="mr-1" />{chapters.find(c => c.id === selectedChapter)?.name}</Badge></>}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <div className="space-y-4">
            <div className="flex gap-4 items-center mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search exams by name or category..."
                  className="pl-10"
                />
              </div>
              <Button onClick={() => { setAddingType('exams'); setAddForm({}); }} className="bg-blue-500 hover:bg-blue-600">
                <Plus size={16} className="mr-2" /> Add Exam
              </Button>
              <Button variant="outline" onClick={loadExams}>
                <RefreshCw size={16} />
              </Button>
            </div>

            {/* Add Exam Form */}
            {addingType === 'exams' && (
              <Card className="p-6 bg-green-500/5 border-green-500/30 mb-4">
                <h4 className="font-bold text-green-500 mb-4 flex items-center gap-2">
                  <Plus size={20} /> Add New Exam - All Fields
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('Exam Name', 'name', addForm, setAddForm, 'text', null, Type)}
                  {renderField('Full Name', 'full_name', addForm, setAddForm, 'text', null, Info)}
                  {renderField('Icon (emoji)', 'icon', addForm, setAddForm, 'text', null, Award)}
                  {renderField('Category', 'category', addForm, setAddForm, 'text', categoryOptions, FolderOpen)}
                  {renderField('Level', 'level', addForm, setAddForm, 'text', levelOptions, Target)}
                  {renderField('Total Questions', 'total_questions', addForm, setAddForm, 'number', null, Hash)}
                  {renderField('Duration (minutes)', 'duration_minutes', addForm, setAddForm, 'number', null, Timer)}
                  {renderField('Marks per Correct', 'marking_correct', addForm, setAddForm, 'number', null, Check)}
                  {renderField('Marks per Wrong (negative)', 'marking_incorrect', addForm, setAddForm, 'number', null, X)}
                  {renderField('Total Marks', 'total_marks', addForm, setAddForm, 'number', null, Award)}
                </div>
                {renderField('Description', 'description', addForm, setAddForm, 'textarea', null, Info)}
                <div className="flex items-center gap-3 mt-3 p-3 bg-background rounded-lg border border-border">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2 cursor-pointer select-none">
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${addForm.is_available ? 'bg-green-500' : 'bg-gray-600'}`}
                      onClick={() => setAddForm(prev => ({ ...prev, is_available: !prev.is_available }))}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${addForm.is_available ? 'translate-x-5' : ''}`} />
                    </div>
                    <span className={addForm.is_available ? 'text-green-500 font-semibold' : 'text-muted-foreground'}>
                      {addForm.is_available ? 'Available Now' : 'Coming Soon'}
                    </span>
                  </label>
                  <span className="text-xs text-muted-foreground">Toggle to make this exam available for students immediately</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-green-500 hover:bg-green-600">
                    {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                    Create Exam
                  </Button>
                  <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                </div>
              </Card>
            )}

            {/* Exams List */}
            <div className="grid gap-3">
              {filteredExams.map(exam => (
                <Card key={exam.id} className={`p-4 border transition-all ${selectedExam === exam.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}>
                  {editingItem?.type === 'exams' && editingItem?.id === exam.id ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-amber-500 flex items-center gap-2">
                          <Edit size={20} /> Editing: {exam.name}
                        </h4>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                            {loading ? <Loader size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
                            Save All
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}><X size={16} /></Button>
                        </div>
                      </div>

                      {/* Availability Toggle */}
                      <div className="flex items-center gap-3 p-3 mb-3 rounded-lg border border-border bg-background">
                        <label className="text-sm font-medium text-foreground flex items-center gap-2 cursor-pointer select-none">
                          <div className={`relative w-11 h-6 rounded-full transition-colors ${editForm.is_available ? 'bg-green-500' : 'bg-gray-600'}`}
                            onClick={() => setEditForm(prev => ({ ...prev, is_available: !prev.is_available }))}
                          >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editForm.is_available ? 'translate-x-5' : ''}`} />
                          </div>
                          <span className={editForm.is_available ? 'text-green-500 font-semibold' : 'text-muted-foreground'}>
                            {editForm.is_available ? 'Available Now' : 'Coming Soon'}
                          </span>
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {editForm.is_available ? 'Students can practice and take tests for this exam' : 'Exam appears as "Coming Soon" on all pages'}
                        </span>
                      </div>

                      {/* Basic Info Section */}
                      <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg mb-3">
                        <p className="text-xs font-semibold text-blue-500 mb-3 uppercase tracking-wider">Basic Information</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {renderField('Exam Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          {renderField('Full Name', 'full_name', editForm, setEditForm, 'text', null, Info)}
                          {renderField('Icon (emoji)', 'icon', editForm, setEditForm, 'text', null, Award)}
                          {renderField('Category', 'category', editForm, setEditForm, 'text', categoryOptions, FolderOpen)}
                          {renderField('Level', 'level', editForm, setEditForm, 'text', levelOptions, Target)}
                        </div>
                        {renderField('Description', 'description', editForm, setEditForm, 'textarea', null, Info)}
                      </div>

                      {/* Exam Pattern Section */}
                      <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg mb-3">
                        <p className="text-xs font-semibold text-purple-500 mb-3 uppercase tracking-wider">Exam Pattern & Marking</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {renderField('Total Questions', 'total_questions', editForm, setEditForm, 'number', null, Hash)}
                          {renderField('Duration (min)', 'duration_minutes', editForm, setEditForm, 'number', null, Timer)}
                          {renderField('Marks/Correct', 'marking_correct', editForm, setEditForm, 'number', null, Check)}
                          {renderField('Marks/Wrong', 'marking_incorrect', editForm, setEditForm, 'number', null, X)}
                          {renderField('Total Marks', 'total_marks', editForm, setEditForm, 'number', null, Award)}
                        </div>
                      </div>

                      {/* Exam Info Section (detailed info for ExamDetails page) */}
                      <details className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg mb-3" open>
                        <summary className="text-xs font-semibold text-green-500 uppercase tracking-wider cursor-pointer flex items-center gap-2">
                          <Info size={14} />
                          Exam Details Page — Full Section Editor
                        </summary>
                        <div className="mt-3 space-y-4">
                          <p className="text-xs text-muted-foreground">Edit every section shown on the Exam Details page. Toggle sections on/off, add/remove items. Data is saved in the exact format the frontend expects.</p>

                          {/* ── Section 1: Header / Basic Info ── */}
                          <details className="border border-border rounded-lg overflow-hidden" open>
                            <summary className="px-4 py-2.5 bg-indigo-500/10 text-sm font-semibold text-indigo-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Info size={14} /> Header & About</span>
                              <span className="text-xs text-muted-foreground">fullName, conductedBy, overview, etc.</span>
                            </summary>
                            <div className="p-4 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Full Name</label>
                                  <Input value={editForm.exam_info?.fullName || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, fullName: e.target.value}}))} placeholder="Joint Entrance Examination (Main)" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Conducted By</label>
                                  <Input value={editForm.exam_info?.conductedBy || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, conductedBy: e.target.value}}))} placeholder="National Testing Agency (NTA)" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Total Candidates (yearly)</label>
                                  <Input value={editForm.exam_info?.totalCandidates || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, totalCandidates: e.target.value}}))} placeholder="12 lakh+" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Exam Frequency</label>
                                  <Input value={editForm.exam_info?.examFrequency || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, examFrequency: e.target.value}}))} placeholder="Twice a year (Jan & Apr)" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Overview</label>
                                <textarea value={editForm.exam_info?.overview || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, overview: e.target.value}}))} className="w-full p-3 rounded-lg bg-background border border-border text-sm min-h-[80px]" placeholder="Brief overview of the exam..." />
                              </div>
                            </div>
                          </details>

                          {/* ── Section 2: Important Dates (key-value object) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-blue-500/10 text-sm font-semibold text-blue-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Calendar size={14} /> Important Dates 2026</span>
                              <span className="text-xs text-muted-foreground">{Object.keys(editForm.exam_info?.importantDates2026 || {}).length} dates</span>
                            </summary>
                            <div className="p-4 space-y-2">
                              <p className="text-xs text-muted-foreground mb-2">Each row = one date entry (key → value). Keys like "registrationOpens", values like "January 2026".</p>
                              {Object.entries(editForm.exam_info?.importantDates2026 || {}).map(([key, value], idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <Input className="flex-1" value={key} onChange={(e) => { const dates = {...(editForm.exam_info?.importantDates2026 || {})}; const val = dates[key]; delete dates[key]; dates[e.target.value] = val; setEditForm(p => ({...p, exam_info: {...p.exam_info, importantDates2026: dates}})); }} placeholder="Key (e.g. registrationOpens)" />
                                  <Input className="flex-1" value={value} onChange={(e) => { const dates = {...(editForm.exam_info?.importantDates2026 || {})}; dates[key] = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, importantDates2026: dates}})); }} placeholder="Value (e.g. January 2026)" />
                                  <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const dates = {...(editForm.exam_info?.importantDates2026 || {})}; delete dates[key]; setEditForm(p => ({...p, exam_info: {...p.exam_info, importantDates2026: dates}})); }}><X size={14} /></Button>
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const dates = {...(editForm.exam_info?.importantDates2026 || {})}; dates[`newDate${Object.keys(dates).length + 1}`] = ''; setEditForm(p => ({...p, exam_info: {...p.exam_info, importantDates2026: dates}})); }}><Plus size={14} className="mr-1" /> Add Date</Button>
                            </div>
                          </details>

                          {/* ── Section 3: Why Prepare (string array) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-emerald-500/10 text-sm font-semibold text-emerald-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Zap size={14} /> Why Prepare</span>
                              <span className="text-xs text-muted-foreground">{(editForm.exam_info?.whyPrepare || []).length} reasons</span>
                            </summary>
                            <div className="p-4 space-y-2">
                              <p className="text-xs text-muted-foreground mb-2">List of motivation reasons. One per row.</p>
                              {(Array.isArray(editForm.exam_info?.whyPrepare) ? editForm.exam_info.whyPrepare : []).map((item, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <span className="text-xs text-muted-foreground w-6 text-center shrink-0">{idx + 1}</span>
                                  <Input className="flex-1" value={item} onChange={(e) => { const arr = [...(editForm.exam_info?.whyPrepare || [])]; arr[idx] = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, whyPrepare: arr}})); }} placeholder="Reason to prepare..." />
                                  <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const arr = [...(editForm.exam_info?.whyPrepare || [])]; arr.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, whyPrepare: arr}})); }}><X size={14} /></Button>
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const arr = [...(editForm.exam_info?.whyPrepare || []), '']; setEditForm(p => ({...p, exam_info: {...p.exam_info, whyPrepare: arr}})); }}><Plus size={14} className="mr-1" /> Add Reason</Button>
                            </div>
                          </details>

                          {/* ── Section 4: Career / Job Opportunities (array of {title, description, companies, salary}) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-purple-500/10 text-sm font-semibold text-purple-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><TrendingUp size={14} /> Career & Job Opportunities</span>
                              <span className="text-xs text-muted-foreground">{(editForm.exam_info?.jobOpportunities || []).length} careers</span>
                            </summary>
                            <div className="p-4 space-y-3">
                              <p className="text-xs text-muted-foreground mb-2">Each career has a title, description, top recruiters, and salary range.</p>
                              {(Array.isArray(editForm.exam_info?.jobOpportunities) ? editForm.exam_info.jobOpportunities : []).map((job, idx) => (
                                <div key={idx} className="p-3 border border-border rounded-lg bg-background space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-purple-500">Career #{idx + 1}</span>
                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { const arr = [...(editForm.exam_info?.jobOpportunities || [])]; arr.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }}><Trash2 size={12} className="mr-1" /> Remove</Button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <Input value={typeof job === 'object' ? (job.title || '') : job} onChange={(e) => { const arr = [...(editForm.exam_info?.jobOpportunities || [])]; arr[idx] = typeof arr[idx] === 'object' ? {...arr[idx], title: e.target.value} : {title: e.target.value, description: '', companies: '', salary: ''}; setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }} placeholder="Job Title (e.g. Software Engineer)" />
                                    <Input value={typeof job === 'object' ? (job.salary || '') : ''} onChange={(e) => { const arr = [...(editForm.exam_info?.jobOpportunities || [])]; arr[idx] = typeof arr[idx] === 'object' ? {...arr[idx], salary: e.target.value} : {title: typeof arr[idx] === 'string' ? arr[idx] : '', description: '', companies: '', salary: e.target.value}; setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }} placeholder="Salary (e.g. ₹8-25 LPA)" />
                                  </div>
                                  <Input value={typeof job === 'object' ? (job.description || '') : ''} onChange={(e) => { const arr = [...(editForm.exam_info?.jobOpportunities || [])]; arr[idx] = typeof arr[idx] === 'object' ? {...arr[idx], description: e.target.value} : {title: typeof arr[idx] === 'string' ? arr[idx] : '', description: e.target.value, companies: '', salary: ''}; setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }} placeholder="Description" />
                                  <Input value={typeof job === 'object' ? (job.companies || '') : ''} onChange={(e) => { const arr = [...(editForm.exam_info?.jobOpportunities || [])]; arr[idx] = typeof arr[idx] === 'object' ? {...arr[idx], companies: e.target.value} : {title: typeof arr[idx] === 'string' ? arr[idx] : '', description: '', companies: e.target.value, salary: ''}; setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }} placeholder="Top Recruiters (e.g. Google, Microsoft, Amazon)" />
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const arr = [...(editForm.exam_info?.jobOpportunities || []), {title: '', description: '', companies: '', salary: ''}]; setEditForm(p => ({...p, exam_info: {...p.exam_info, jobOpportunities: arr}})); }}><Plus size={14} className="mr-1" /> Add Career</Button>
                            </div>
                          </details>

                          {/* ── Section 5: Top Colleges (array of strings or {name, cutoff, avgPackage}) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-amber-500/10 text-sm font-semibold text-amber-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Award size={14} /> Top Colleges / Institutes</span>
                              <span className="text-xs text-muted-foreground">{(editForm.exam_info?.topColleges || []).length} colleges</span>
                            </summary>
                            <div className="p-4 space-y-2">
                              <p className="text-xs text-muted-foreground mb-2">College name with optional cutoff/package details.</p>
                              {(Array.isArray(editForm.exam_info?.topColleges) ? editForm.exam_info.topColleges : []).map((college, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <Input className="flex-[2]" value={typeof college === 'string' ? college : (college?.name || '')} onChange={(e) => { const arr = [...(editForm.exam_info?.topColleges || [])]; arr[idx] = typeof arr[idx] === 'object' ? {...arr[idx], name: e.target.value} : e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, topColleges: arr}})); }} placeholder="College name" />
                                  <Input className="flex-1" value={typeof college === 'object' ? (college?.cutoff || '') : ''} onChange={(e) => { const arr = [...(editForm.exam_info?.topColleges || [])]; const cur = typeof arr[idx] === 'string' ? {name: arr[idx]} : {...arr[idx]}; cur.cutoff = e.target.value; arr[idx] = cur; setEditForm(p => ({...p, exam_info: {...p.exam_info, topColleges: arr}})); }} placeholder="Cutoff (optional)" />
                                  <Input className="flex-1" value={typeof college === 'object' ? (college?.avgPackage || '') : ''} onChange={(e) => { const arr = [...(editForm.exam_info?.topColleges || [])]; const cur = typeof arr[idx] === 'string' ? {name: arr[idx]} : {...arr[idx]}; cur.avgPackage = e.target.value; arr[idx] = cur; setEditForm(p => ({...p, exam_info: {...p.exam_info, topColleges: arr}})); }} placeholder="Avg Package (optional)" />
                                  <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const arr = [...(editForm.exam_info?.topColleges || [])]; arr.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, topColleges: arr}})); }}><X size={14} /></Button>
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const arr = [...(editForm.exam_info?.topColleges || []), '']; setEditForm(p => ({...p, exam_info: {...p.exam_info, topColleges: arr}})); }}><Plus size={14} className="mr-1" /> Add College</Button>
                            </div>
                          </details>

                          {/* ── Section 6: Exam Pattern (object with mode, papers, sections) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-indigo-500/10 text-sm font-semibold text-indigo-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><BarChart3 size={14} /> Exam Pattern Details</span>
                              <span className="text-xs text-muted-foreground">mode, papers, sections</span>
                            </summary>
                            <div className="p-4 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Mode</label>
                                  <Input value={editForm.exam_info?.examPattern?.mode || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), mode: e.target.value}}}))} placeholder="Online (CBT)" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Papers</label>
                                  <Input value={editForm.exam_info?.examPattern?.papers || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), papers: e.target.value}}}))} placeholder="Paper 1 & Paper 2" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Total Questions (pattern)</label>
                                  <Input type="number" value={editForm.exam_info?.examPattern?.totalQuestions || ''} onChange={(e) => setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), totalQuestions: parseInt(e.target.value) || 0}}}))} placeholder="90" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Sections</label>
                                <p className="text-xs text-muted-foreground mb-2">Define sections with name, questions, duration, and marks.</p>
                                {(editForm.exam_info?.examPattern?.sections || []).map((sec, idx) => (
                                  <div key={idx} className="flex gap-2 items-center mb-2">
                                    <Input className="flex-[2]" value={sec.name || ''} onChange={(e) => { const secs = [...(editForm.exam_info?.examPattern?.sections || [])]; secs[idx] = {...secs[idx], name: e.target.value}; setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }} placeholder="Section name" />
                                    <Input className="flex-1" type="number" value={sec.questions || ''} onChange={(e) => { const secs = [...(editForm.exam_info?.examPattern?.sections || [])]; secs[idx] = {...secs[idx], questions: parseInt(e.target.value) || 0}; setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }} placeholder="Qs" />
                                    <Input className="flex-1" type="number" value={sec.duration_minutes || ''} onChange={(e) => { const secs = [...(editForm.exam_info?.examPattern?.sections || [])]; secs[idx] = {...secs[idx], duration_minutes: parseInt(e.target.value) || 0}; setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }} placeholder="Mins" />
                                    <Input className="flex-1" type="number" value={sec.marks || ''} onChange={(e) => { const secs = [...(editForm.exam_info?.examPattern?.sections || [])]; secs[idx] = {...secs[idx], marks: parseInt(e.target.value) || 0}; setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }} placeholder="Marks" />
                                    <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const secs = [...(editForm.exam_info?.examPattern?.sections || [])]; secs.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }}><X size={14} /></Button>
                                  </div>
                                ))}
                                <Button size="sm" variant="outline" onClick={() => { const secs = [...(editForm.exam_info?.examPattern?.sections || []), {name: '', questions: 0, duration_minutes: 0, marks: 0}]; setEditForm(p => ({...p, exam_info: {...p.exam_info, examPattern: {...(p.exam_info?.examPattern || {}), sections: secs}}})); }}><Plus size={14} className="mr-1" /> Add Section</Button>
                              </div>
                            </div>
                          </details>

                          {/* ── Section 7: Eligibility (structured object) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-emerald-500/10 text-sm font-semibold text-emerald-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Check size={14} /> Eligibility Criteria</span>
                              <span className="text-xs text-muted-foreground">{Object.values(editForm.exam_info?.eligibility || {}).filter(Boolean).length} fields set</span>
                            </summary>
                            <div className="p-4 space-y-3">
                              <p className="text-xs text-muted-foreground mb-2">Structured eligibility data. Fill relevant fields.</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Age Limit</label>
                                  <Input value={typeof editForm.exam_info?.eligibility === 'object' ? (editForm.exam_info.eligibility.age || '') : ''} onChange={(e) => { const elig = typeof editForm.exam_info?.eligibility === 'object' ? {...editForm.exam_info.eligibility} : {}; elig.age = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, eligibility: elig}})); }} placeholder="No upper age limit / 17-25 years" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Qualification</label>
                                  <Input value={typeof editForm.exam_info?.eligibility === 'object' ? (editForm.exam_info.eligibility.qualification || '') : ''} onChange={(e) => { const elig = typeof editForm.exam_info?.eligibility === 'object' ? {...editForm.exam_info.eligibility} : {}; elig.qualification = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, eligibility: elig}})); }} placeholder="Class 12th passed or appearing" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Minimum Marks</label>
                                  <Input value={typeof editForm.exam_info?.eligibility === 'object' ? (editForm.exam_info.eligibility.minimumMarks || '') : ''} onChange={(e) => { const elig = typeof editForm.exam_info?.eligibility === 'object' ? {...editForm.exam_info.eligibility} : {}; elig.minimumMarks = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, eligibility: elig}})); }} placeholder="75% in 12th (65% for SC/ST)" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Number of Attempts</label>
                                  <Input value={typeof editForm.exam_info?.eligibility === 'object' ? (editForm.exam_info.eligibility.attempts || '') : ''} onChange={(e) => { const elig = typeof editForm.exam_info?.eligibility === 'object' ? {...editForm.exam_info.eligibility} : {}; elig.attempts = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, eligibility: elig}})); }} placeholder="3 consecutive years" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Year of Passing</label>
                                  <Input value={typeof editForm.exam_info?.eligibility === 'object' ? (editForm.exam_info.eligibility.yearOfPassing || '') : ''} onChange={(e) => { const elig = typeof editForm.exam_info?.eligibility === 'object' ? {...editForm.exam_info.eligibility} : {}; elig.yearOfPassing = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, eligibility: elig}})); }} placeholder="2024, 2025 or appearing in 2026" />
                                </div>
                              </div>
                            </div>
                          </details>

                          {/* ── Section 8: Preparation Tips (string array) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-violet-500/10 text-sm font-semibold text-violet-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Target size={14} /> Preparation Tips</span>
                              <span className="text-xs text-muted-foreground">{(editForm.exam_info?.preparationTips || []).length} tips</span>
                            </summary>
                            <div className="p-4 space-y-2">
                              {(Array.isArray(editForm.exam_info?.preparationTips) ? editForm.exam_info.preparationTips : []).map((tip, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <span className="text-xs text-muted-foreground w-6 text-center shrink-0">{idx + 1}</span>
                                  <Input className="flex-1" value={tip} onChange={(e) => { const arr = [...(editForm.exam_info?.preparationTips || [])]; arr[idx] = e.target.value; setEditForm(p => ({...p, exam_info: {...p.exam_info, preparationTips: arr}})); }} placeholder="Preparation tip..." />
                                  <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const arr = [...(editForm.exam_info?.preparationTips || [])]; arr.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, preparationTips: arr}})); }}><X size={14} /></Button>
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const arr = [...(editForm.exam_info?.preparationTips || []), '']; setEditForm(p => ({...p, exam_info: {...p.exam_info, preparationTips: arr}})); }}><Plus size={14} className="mr-1" /> Add Tip</Button>
                            </div>
                          </details>

                          {/* ── Section 9: College Packages & Salary ({college, avgPackage, highestPackage}) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-green-500/10 text-sm font-semibold text-green-600 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><TrendingUp size={14} /> Salary & Placement Packages</span>
                              <span className="text-xs text-muted-foreground">{(editForm.exam_info?.collegePackages || []).length} entries</span>
                            </summary>
                            <div className="p-4 space-y-2">
                              <p className="text-xs text-muted-foreground mb-2">College-wise placement data. Remove all entries to hide this section from students.</p>
                              {(Array.isArray(editForm.exam_info?.collegePackages) ? editForm.exam_info.collegePackages : []).map((pkg, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <Input className="flex-[2]" value={pkg?.college || ''} onChange={(e) => { const arr = [...(editForm.exam_info?.collegePackages || [])]; arr[idx] = {...(arr[idx] || {}), college: e.target.value}; setEditForm(p => ({...p, exam_info: {...p.exam_info, collegePackages: arr}})); }} placeholder="College name" />
                                  <Input className="flex-1" value={pkg?.avgPackage || ''} onChange={(e) => { const arr = [...(editForm.exam_info?.collegePackages || [])]; arr[idx] = {...(arr[idx] || {}), avgPackage: e.target.value}; setEditForm(p => ({...p, exam_info: {...p.exam_info, collegePackages: arr}})); }} placeholder="Avg Package" />
                                  <Input className="flex-1" value={pkg?.highestPackage || ''} onChange={(e) => { const arr = [...(editForm.exam_info?.collegePackages || [])]; arr[idx] = {...(arr[idx] || {}), highestPackage: e.target.value}; setEditForm(p => ({...p, exam_info: {...p.exam_info, collegePackages: arr}})); }} placeholder="Highest Package" />
                                  <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => { const arr = [...(editForm.exam_info?.collegePackages || [])]; arr.splice(idx, 1); setEditForm(p => ({...p, exam_info: {...p.exam_info, collegePackages: arr}})); }}><X size={14} /></Button>
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const arr = [...(editForm.exam_info?.collegePackages || []), {college: '', avgPackage: '', highestPackage: ''}]; setEditForm(p => ({...p, exam_info: {...p.exam_info, collegePackages: arr}})); }}><Plus size={14} className="mr-1" /> Add College Package</Button>
                            </div>
                          </details>

                          {/* ── Section 10: Syllabus (subject → topics mapping) ── */}
                          <details className="border border-border rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-teal-500/10 text-sm font-semibold text-teal-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><BookOpen size={14} /> Syllabus (Subject → Topics)</span>
                              <span className="text-xs text-muted-foreground">{Object.keys(editForm.exam_info?.syllabus || {}).length} subjects</span>
                            </summary>
                            <div className="p-4 space-y-3">
                              <p className="text-xs text-muted-foreground mb-2">Map each subject to a list of topics. This supplements the chapter data from the database.</p>
                              {Object.entries(editForm.exam_info?.syllabus || {}).map(([subject, topics], sIdx) => (
                                <div key={sIdx} className="p-3 border border-border rounded-lg bg-background">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Input className="flex-1 font-semibold" value={subject} onChange={(e) => { const syl = {...(editForm.exam_info?.syllabus || {})}; const t = syl[subject]; delete syl[subject]; syl[e.target.value] = t; setEditForm(p => ({...p, exam_info: {...p.exam_info, syllabus: syl}})); }} placeholder="Subject name" />
                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { const syl = {...(editForm.exam_info?.syllabus || {})}; delete syl[subject]; setEditForm(p => ({...p, exam_info: {...p.exam_info, syllabus: syl}})); }}><Trash2 size={12} className="mr-1" /> Remove Subject</Button>
                                  </div>
                                  <textarea
                                    value={Array.isArray(topics) ? topics.join('\n') : ''}
                                    onChange={(e) => { const syl = {...(editForm.exam_info?.syllabus || {})}; syl[subject] = e.target.value.split('\n').filter(Boolean); setEditForm(p => ({...p, exam_info: {...p.exam_info, syllabus: syl}})); }}
                                    className="w-full p-2 rounded-lg bg-muted border border-border text-xs min-h-[60px]"
                                    placeholder="One topic per line&#10;Kinematics&#10;Laws of Motion&#10;Thermodynamics"
                                  />
                                </div>
                              ))}
                              <Button size="sm" variant="outline" onClick={() => { const syl = {...(editForm.exam_info?.syllabus || {})}; syl[`Subject ${Object.keys(syl).length + 1}`] = []; setEditForm(p => ({...p, exam_info: {...p.exam_info, syllabus: syl}})); }}><Plus size={14} className="mr-1" /> Add Subject</Button>
                            </div>
                          </details>

                          {/* ── Section Visibility Toggles ── */}
                          <details className="border border-red-500/20 rounded-lg overflow-hidden">
                            <summary className="px-4 py-2.5 bg-red-500/10 text-sm font-semibold text-red-500 cursor-pointer flex items-center justify-between">
                              <span className="flex items-center gap-2"><Eye size={14} /> Section Visibility (hide unwanted sections)</span>
                            </summary>
                            <div className="p-4">
                              <p className="text-xs text-muted-foreground mb-3">Toggle sections off to hide them from the student-facing exam details page. Removing all items from a section also hides it.</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {[
                                  {key: 'hideAbout', label: 'About Section'},
                                  {key: 'hideDates', label: 'Important Dates'},
                                  {key: 'hideWhyPrepare', label: 'Why Prepare'},
                                  {key: 'hideCareer', label: 'Career Opportunities'},
                                  {key: 'hideColleges', label: 'Top Colleges'},
                                  {key: 'hidePattern', label: 'Exam Pattern'},
                                  {key: 'hideEligibility', label: 'Eligibility'},
                                  {key: 'hideTips', label: 'Preparation Tips'},
                                  {key: 'hidePackages', label: 'Salary & Packages'},
                                  {key: 'hideSyllabus', label: 'Syllabus'},
                                ].map(({key, label}) => (
                                  <label key={key} className="flex items-center gap-2 p-2 rounded-lg border border-border hover:bg-muted cursor-pointer select-none">
                                    <div className={`relative w-9 h-5 rounded-full transition-colors ${editForm.exam_info?.[key] ? 'bg-red-500' : 'bg-green-500'}`}
                                      onClick={(e) => { e.preventDefault(); setEditForm(p => ({...p, exam_info: {...p.exam_info, [key]: !p.exam_info?.[key]}})); }}>
                                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${editForm.exam_info?.[key] ? 'translate-x-4' : ''}`} />
                                    </div>
                                    <span className={`text-xs ${editForm.exam_info?.[key] ? 'text-red-500 line-through' : 'text-foreground'}`}>{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </details>
                        </div>
                      </details>

                      <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                        <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                          {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                          Save All Changes
                        </Button>
                        <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-3 cursor-pointer flex-1" 
                        onClick={() => { loadSubjects(exam.id); setSelectedSubject(null); setChapters([]); }}
                      >
                        <span className="text-3xl">{exam.icon || '📚'}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-foreground">{exam.name}</h3>
                            {exam.full_name && <span className="text-xs text-muted-foreground hidden md:inline">({exam.full_name})</span>}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs cursor-pointer select-none ${exam.is_available ? 'bg-green-500/10 text-green-500 border-green-500/30 hover:bg-green-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20'}`}
                              onClick={(e) => { e.stopPropagation(); toggleExamAvailability(exam); }}
                              title={exam.is_available ? 'Click to mark as Coming Soon' : 'Click to make Available'}
                            >
                              {exam.is_available ? '✓ Available' : '⏳ Coming Soon'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{exam.category}</Badge>
                            <Badge variant="outline" className="text-xs">{exam.level}</Badge>
                            <Badge variant="outline" className="text-xs">{exam.total_questions} Q</Badge>
                            <Badge variant="outline" className="text-xs">{exam.duration_minutes} min</Badge>
                            <Badge variant="outline" className="text-xs text-green-500">+{exam.marking_correct}</Badge>
                            <Badge variant="outline" className="text-xs text-red-500">{exam.marking_incorrect}</Badge>
                          </div>
                        </div>
                        {selectedExam === exam.id && <ChevronRight size={20} className="text-primary" />}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('exams', exam); }}>
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); handleDelete('exams', exam.id, exam.name); }}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="space-y-4">
            {!selectedExam ? (
              <Card className="p-8 text-center border-dashed border-2">
                <AlertCircle size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">No Exam Selected</p>
                <p className="text-muted-foreground mb-4">Please select an exam first from the Exams tab by clicking on it</p>
                <Button onClick={() => setActiveTab('exams')}>
                  <BookOpen size={16} className="mr-2" /> Go to Exams
                </Button>
              </Card>
            ) : (
              <>
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Subjects for: {exams.find(e => e.id === selectedExam)?.name}</h3>
                    <p className="text-sm text-muted-foreground">{subjects.length} subjects found</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setAddingType('subjects'); setAddForm({}); }} className="bg-green-500 hover:bg-green-600">
                      <Plus size={16} className="mr-2" /> Add Subject
                    </Button>
                    <Button variant="outline" onClick={() => loadSubjects(selectedExam)}>
                      <RefreshCw size={16} />
                    </Button>
                  </div>
                </div>

                {addingType === 'subjects' && (
                  <Card className="p-6 bg-green-500/5 border-green-500/30">
                    <h4 className="font-bold text-green-500 mb-4 flex items-center gap-2">
                      <Plus size={20} /> Add New Subject
                    </h4>
                    {renderField('Subject Name', 'name', addForm, setAddForm, 'text', null, Type)}
                    <div className="grid grid-cols-2 gap-4">
                      {renderField('Questions Count', 'questions_count', addForm, setAddForm, 'number', 'Number of questions in this section', Hash)}
                      {renderField('Total Marks', 'marks', addForm, setAddForm, 'number', 'Total marks for this section', Award)}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-green-500 hover:bg-green-600">
                        {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                        Create Subject
                      </Button>
                      <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                    </div>
                  </Card>
                )}

                <div className="grid gap-3">
                  {subjects.map(subject => (
                    <Card key={subject.id} className={`p-4 border transition-all ${selectedSubject === subject.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}>
                      {editingItem?.type === 'subjects' && editingItem?.id === subject.id ? (
                        <div>
                          <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                            <Edit size={20} /> Editing: {subject.name}
                          </h4>
                          {renderField('Subject Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          <div className="grid grid-cols-2 gap-4">
                            {renderField('Questions Count', 'questions_count', editForm, setEditForm, 'number', null, Hash)}
                            {renderField('Total Marks', 'marks', editForm, setEditForm, 'number', null, Award)}
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                              <Save size={16} className="mr-2" /> Save
                            </Button>
                            <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1" 
                            onClick={() => loadChapters(subject.id)}
                          >
                            <Layers size={24} className="text-green-500" />
                            <div>
                              <span className="font-medium text-foreground">{subject.name}</span>
                              <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                                <span>{subject.questions_count || 25} Qs</span>
                                <span>{subject.marks || 100} marks</span>
                              </div>
                            </div>
                            {selectedSubject === subject.id && <ChevronRight size={20} className="text-primary" />}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('subjects', subject); }}>
                              <Edit size={14} className="mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); handleDelete('subjects', subject.id, subject.name); }}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                  {subjects.length === 0 && (
                    <Card className="p-8 text-center border-dashed">
                      <p className="text-muted-foreground">No subjects yet. Add one above!</p>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Chapters Tab */}
        {activeTab === 'chapters' && (
          <div className="space-y-4">
            {!selectedSubject ? (
              <Card className="p-8 text-center border-dashed border-2">
                <AlertCircle size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">No Subject Selected</p>
                <p className="text-muted-foreground mb-4">Please select an exam first, then select a subject by clicking on it</p>
                <Button onClick={() => setActiveTab('subjects')}>
                  <Layers size={16} className="mr-2" /> Go to Subjects
                </Button>
              </Card>
            ) : (
              <>
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Chapters for: {subjects.find(s => s.id === selectedSubject)?.name}</h3>
                    <p className="text-sm text-muted-foreground">{chapters.length} chapters found</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setAddingType('chapters'); setAddForm({}); }} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                      <Plus size={16} className="mr-2" /> Add Chapter
                    </Button>
                    <Button variant="outline" onClick={() => loadChapters(selectedSubject)}>
                      <RefreshCw size={16} />
                    </Button>
                  </div>
                </div>

                {addingType === 'chapters' && (
                  <Card className="p-6 bg-yellow-500/5 border-yellow-500/30">
                    <h4 className="font-bold text-yellow-600 mb-4 flex items-center gap-2">
                      <Plus size={20} /> Add New Chapter
                    </h4>
                    {renderField('Chapter Name', 'name', addForm, setAddForm, 'text', null, Type)}
                    {renderField('Notes Content (optional)', 'notes_content', addForm, setAddForm, 'textarea', null, FileText)}
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                        <Check size={16} className="mr-2" /> Create Chapter
                      </Button>
                      <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                    </div>
                  </Card>
                )}

                <div className="grid gap-3">
                  {chapters.map(chapter => (
                    <Card key={chapter.id} className={`p-4 border transition-all ${selectedChapter === chapter.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      {editingItem?.type === 'chapters' && editingItem?.id === chapter.id ? (
                        <div>
                          <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                            <Edit size={20} /> Editing: {chapter.name}
                          </h4>
                          {renderField('Chapter Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          {renderField('Notes Content', 'notes_content', editForm, setEditForm, 'textarea', null, FileText)}
                          <div className="flex gap-2 mt-4">
                            <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                              <Save size={16} className="mr-2" /> Save
                            </Button>
                            <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => setSelectedChapter(chapter.id)}
                          >
                            <FolderOpen size={24} className="text-yellow-500" />
                            <div>
                              <span className="font-medium text-foreground">{chapter.name}</span>
                              {chapter.notes_content && (
                                <p className="text-xs text-muted-foreground mt-1">Has notes content</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('chapters', chapter); }}>
                              <Edit size={14} className="mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); handleDelete('chapters', chapter.id, chapter.name); }}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                  {chapters.length === 0 && (
                    <Card className="p-8 text-center border-dashed">
                      <p className="text-muted-foreground">No chapters yet. Add one above!</p>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Questions Tab - Redirects to Advanced Question Manager */}
        {activeTab === 'questions' && (
          <Card className="p-12 text-center bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-green-500/10 border-purple-500/30">
            <Zap className="mx-auto mb-4 text-purple-500" size={64} />
            <h3 className="text-2xl font-bold text-foreground mb-2">Advanced Question Manager</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We've upgraded the question manager with powerful features: smooth pagination for 1,000,000+ questions,
              bulk operations, advanced filtering, image management, and AI-powered difficulty detection.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button 
                onClick={() => navigate('/admin/questions')}
                className="bg-purple-500 hover:bg-purple-600 gap-2 text-white px-8 py-6 text-lg"
                size="lg"
              >
                <Zap size={20} />
                Open Advanced Question Manager
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="p-4 bg-card rounded-lg border border-border">
                <CheckCircle className="mx-auto mb-2 text-green-500" size={24} />
                <h4 className="font-semibold mb-1">Smooth Performance</h4>
                <p className="text-xs text-muted-foreground">Handles 1,000,000+ questions without lag</p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <Trash2 className="mx-auto mb-2 text-red-500" size={24} />
                <h4 className="font-semibold mb-1">Bulk Operations</h4>
                <p className="text-xs text-muted-foreground">Delete or update multiple questions at once</p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <Upload className="mx-auto mb-2 text-blue-500" size={24} />
                <h4 className="font-semibold mb-1">Image Management</h4>
                <p className="text-xs text-muted-foreground">Drag, drop, or paste images directly</p>
              </div>
            </div>
          </Card>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-lg">{users.length} Users</h3>
                <p className="text-sm text-muted-foreground">Manage user accounts & subscriptions</p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48"
                />
                <Button variant="outline" onClick={loadUsers}>
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>
            </div>

            {/* User Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 border-blue-500/30 bg-blue-500/5">
                <p className="text-2xl font-bold text-blue-400">{users.length}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </Card>
              <Card className="p-4 border-amber-500/30 bg-amber-500/5">
                <p className="text-2xl font-bold text-amber-400">{users.filter(u => u.premium).length}</p>
                <p className="text-xs text-muted-foreground">Premium Users</p>
              </Card>
              <Card className="p-4 border-red-500/30 bg-red-500/5">
                <p className="text-2xl font-bold text-red-400">{users.filter(u => u.role === 'admin').length}</p>
                <p className="text-xs text-muted-foreground">Admins</p>
              </Card>
              <Card className="p-4 border-green-500/30 bg-green-500/5">
                <p className="text-2xl font-bold text-green-400">{users.filter(u => u.last_active && new Date(u.last_active) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}</p>
                <p className="text-xs text-muted-foreground">Active (7 days)</p>
              </Card>
            </div>

            <div className="space-y-3">
              {users.filter(u => 
                !searchTerm || 
                u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                u.email?.toLowerCase().includes(searchTerm.toLowerCase())
              ).map(u => (
                <Card key={u.id} className="p-4 border border-border">
                  {editingItem?.type === 'users' && editingItem?.id === u.id ? (
                    <div>
                      <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                        <Edit size={20} /> Editing: {u.name}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderField('Name', 'name', editForm, setEditForm, 'text', null, Type)}
                        {renderField('Email', 'email', editForm, setEditForm, 'email', null, null)}
                        {renderField('Role', 'role', editForm, setEditForm, 'text', roleOptions, Shield)}
                        {renderField('Points', 'points', editForm, setEditForm, 'number', null, Award)}
                        {renderField('Streak', 'streak', editForm, setEditForm, 'number', null, Zap)}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                          {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-foreground">{u.name}</span>
                            <Badge variant={u.role === 'admin' ? 'default' : 'outline'} className={`text-xs ${u.role === 'admin' ? 'bg-red-500' : ''}`}>
                              {u.role}
                            </Badge>
                            {u.premium && (
                              <Badge className="bg-amber-500 text-xs">
                                ⭐ {u.premium_plan || 'Premium'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                          <div className="flex gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                            <span>🏆 {u.points} pts</span>
                            <span>🔥 {u.streak} streak</span>
                            <span>📝 {u.tests_taken || 0} tests</span>
                            {u.discord_username && <span>💬 {u.discord_username}</span>}
                            {u.created_at && <span>📅 Joined: {new Date(u.created_at).toLocaleDateString()}</span>}
                          </div>
                          
                          {/* Subscription Details */}
                          {u.premium && (
                            <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <div className="flex items-center gap-4 text-xs flex-wrap">
                                <span className="text-amber-400 font-medium">
                                  Plan: {u.premium_plan?.toUpperCase() || 'PREMIUM'}
                                </span>
                                {u.premium_started_at && (
                                  <span className="text-muted-foreground">
                                    Started: {new Date(u.premium_started_at).toLocaleDateString()}
                                  </span>
                                )}
                                {u.premium_expires_at && (
                                  <span className={`font-medium ${u.days_remaining <= 7 ? 'text-red-400' : 'text-green-400'}`}>
                                    {u.days_remaining > 0 
                                      ? `Expires in ${u.days_remaining} days`
                                      : 'Expired'
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit('users', u)}>
                            <Edit size={14} className="mr-1" /> Edit
                          </Button>
                          {u.role !== 'admin' && (
                            <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-500/10" onClick={() => handleDelete('users', u.id, u.name)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                        
                        {/* Subscription Management */}
                        <div className="flex gap-1 flex-wrap">
                          {!u.premium ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/users/${u.id}/subscription`, { action: 'add', plan: 'monthly' });
                                    toast.success(`1 Month Premium added for ${u.name}`);
                                    loadUsers();
                                  } catch (err) {
                                    toast.error('Failed to add premium');
                                  }
                                }}
                              >
                                +1 Month
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/users/${u.id}/subscription`, { action: 'add', plan: 'yearly' });
                                    toast.success(`1 Year Premium added for ${u.name}`);
                                    loadUsers();
                                  } catch (err) {
                                    toast.error('Failed to add premium');
                                  }
                                }}
                              >
                                +1 Year
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/users/${u.id}/subscription`, { action: 'add', plan: 'lifetime' });
                                    toast.success(`Lifetime Premium added for ${u.name}`);
                                    loadUsers();
                                  } catch (err) {
                                    toast.error('Failed to add premium');
                                  }
                                }}
                              >
                                Lifetime
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/users/${u.id}/subscription`, { action: 'extend', plan: 'monthly' });
                                    toast.success(`Extended 1 Month for ${u.name}`);
                                    loadUsers();
                                  } catch (err) {
                                    toast.error('Failed to extend');
                                  }
                                }}
                              >
                                +1 Month
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={async () => {
                                  if (!window.confirm(`Remove premium from ${u.name}?`)) return;
                                  try {
                                    await api.post(`/admin/users/${u.id}/subscription`, { action: 'remove' });
                                    toast.success(`Premium removed from ${u.name}`);
                                    loadUsers();
                                  } catch (err) {
                                    toast.error('Failed to remove premium');
                                  }
                                }}
                              >
                                Remove
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
              {users.length === 0 && (
                <Card className="p-8 text-center border-dashed">
                  <Loader size={24} className="animate-spin mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading users...</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Staff Management Tab */}
        {activeTab === 'staff' && (
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                <Shield className="text-red-400" size={24} />
                Staff Management
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Create and manage <strong>Content Editor</strong> accounts — they can add/edit questions and use import tools, but cannot manage exams, users, or staff.
              </p>
            </div>

            {/* Create Staff Form */}
            <Card className="p-6 border-red-500/30 bg-red-500/5">
              <h4 className="font-semibold text-red-400 mb-4 flex items-center gap-2">
                <Plus size={16} />
                Add Content Editor
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                  <Input
                    value={staffForm.name}
                    onChange={e => setStaffForm({ ...staffForm, name: e.target.value })}
                    placeholder="Full name"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={staffForm.email}
                    onChange={e => setStaffForm({ ...staffForm, email: e.target.value })}
                    placeholder="email@example.com"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Password</label>
                  <Input
                    type="password"
                    value={staffForm.password}
                    onChange={e => setStaffForm({ ...staffForm, password: e.target.value })}
                    placeholder="Min 8 characters"
                    className="text-sm"
                  />
                </div>
              </div>
              <Button
                onClick={createStaff}
                disabled={staffLoading}
                className="flex items-center gap-2"
                size="sm"
              >
                {staffLoading ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                Create Content Editor
              </Button>
            </Card>

            {/* Staff List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-foreground">Current Staff</h4>
                <Button variant="ghost" size="sm" onClick={loadStaff} title="Refresh">
                  <RefreshCw size={14} />
                </Button>
              </div>
              {staffLoading ? (
                <Card className="p-8 text-center">
                  <Loader size={24} className="animate-spin mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">Loading staff...</p>
                </Card>
              ) : staff.length === 0 ? (
                <Card className="p-8 text-center border-dashed">
                  <Shield size={24} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">No content editors yet. Create one above.</p>
                </Card>
              ) : (
                staff.map(member => (
                  <Card key={member.id} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center">
                        <Shield size={16} className="text-red-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-foreground">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs border-red-400/50 text-red-400">
                        Content Editor
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => deleteStaff(member.id, member.email)}
                        title="Remove staff member"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Image Questions Upload Tab */}
        {activeTab === 'image-upload' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Image className="text-purple-500" size={24} />
                  Image-Based Questions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload screenshots - <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Ctrl+V</kbd> to paste from clipboard!
                </p>
              </div>
              <Badge variant="outline" className="text-green-400 border-green-500/50">
                Session: {imageQuestions.length} added
              </Badge>
            </div>

            {/* Step 1: Select Exam, Subject, Chapter */}
            <Card className="p-6 border-purple-500/30 bg-purple-500/5">
              <h4 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">1</span>
                Select Location
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Exam</label>
                  <select
                    value={selectedExam || ''}
                    onChange={(e) => {
                      setSelectedExam(e.target.value);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/exams/${e.target.value}/subjects`).then(res => setSubjects(res.data || []));
                      }
                    }}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                  <select
                    value={selectedSubject || ''}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/subjects/${e.target.value}/chapters`).then(res => setChapters(res.data || []));
                      }
                    }}
                    disabled={!selectedExam}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Chapter</label>
                  <select
                    value={selectedChapter || ''}
                    onChange={(e) => setSelectedChapter(e.target.value)}
                    disabled={!selectedSubject}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </Card>

            {/* Step 2: Upload Question Image - Enhanced */}
            {selectedChapter && (
              <Card 
                className="p-6 border-blue-500/30 bg-blue-500/5"
                tabIndex={0}
                onPaste={async (e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    for (const item of items) {
                      if (item.type.startsWith('image/')) {
                        e.preventDefault();
                        const file = item.getAsFile();
                        if (file) {
                          try {
                            toast.loading('Compressing...', { id: 'paste-upload' });
                            const compressedFile = await compressImage(file);
                            const formData = new FormData();
                            formData.append('file', compressedFile);
                            formData.append('category', 'questions');
                            toast.loading('Uploading...', { id: 'paste-upload' });
                            const res = await api.post('/admin/upload/image', formData);
                            setCurrentImageQuestion(prev => ({ ...prev, question_image: res.data.url }));
                            toast.success('Image pasted!', { id: 'paste-upload' });
                          } catch (err) {
                            toast.error('Failed to upload', { id: 'paste-upload' });
                          }
                        }
                        break;
                      }
                    }
                  }
                }}
              >
                <h4 className="font-semibold text-blue-400 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">2</span>
                  Upload Question
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    (Paste with Ctrl+V or click to browse)
                  </span>
                </h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Image Upload Zone - Compact */}
                  <div>
                    <div 
                      className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-blue-500 transition-colors cursor-pointer relative min-h-[180px] flex items-center justify-center"
                      onClick={() => document.getElementById('question-image-input').click()}
                    >
                      {currentImageQuestion.question_image ? (
                        <div className="relative w-full">
                          <img 
                            src={typeof currentImageQuestion.question_image === 'string' 
                              ? (currentImageQuestion.question_image.startsWith('/') 
                                ? `http://localhost:8000${currentImageQuestion.question_image}` 
                                : currentImageQuestion.question_image)
                              : URL.createObjectURL(currentImageQuestion.question_image)} 
                            alt="Question" 
                            className="max-h-48 mx-auto rounded-lg"
                          />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageQuestion(prev => ({ ...prev, question_image: null }));
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Image size={36} className="mx-auto text-muted-foreground mb-2" />
                          <p className="text-muted-foreground text-sm">Click or <strong>Ctrl+V</strong> to paste</p>
                        </div>
                      )}
                    </div>
                    <input
                      id="question-image-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            toast.loading('Compressing...');
                            const compressedFile = await compressImage(file);
                            const formData = new FormData();
                            formData.append('file', compressedFile);
                            formData.append('category', 'questions');
                            const res = await api.post('/admin/upload/image', formData);
                            setCurrentImageQuestion(prev => ({ ...prev, question_image: res.data.url }));
                            toast.success('Image uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload image');
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Quick Answer Selection */}
                  <div className="space-y-3">
                    {/* Correct Answer - Big Buttons */}
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">Correct Answer (click or press 1-4)</label>
                      <div className="flex gap-2">
                        {['A', 'B', 'C', 'D'].map((label, idx) => (
                          <button
                            key={label}
                            onClick={() => setCurrentImageQuestion(prev => ({ ...prev, correct_answer: label }))}
                            className={`flex-1 py-3 rounded-xl text-lg font-bold transition-all ${
                              currentImageQuestion.correct_answer === label 
                                ? 'bg-green-500 text-white scale-105 shadow-lg shadow-green-500/30' 
                                : 'bg-muted hover:bg-green-500/20 text-foreground'
                            }`}
                          >
                            {label}
                            <span className="text-xs opacity-50 ml-1">({idx + 1})</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quick Categories */}
                    <div className="flex gap-3">
                      <label className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-border cursor-pointer hover:border-blue-500 transition-colors">
                        <input
                          type="checkbox"
                          checked={currentImageQuestion.is_pyq}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, is_pyq: e.target.checked }))}
                          className="w-5 h-5 accent-blue-500"
                        />
                        <span className="text-blue-400 font-medium">📜 PYQ</span>
                      </label>
                      <label className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-border cursor-pointer hover:border-amber-500 transition-colors">
                        <input
                          type="checkbox"
                          checked={currentImageQuestion.is_important}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, is_important: e.target.checked }))}
                          className="w-5 h-5 accent-amber-500"
                        />
                        <span className="text-amber-400 font-medium">⭐ Important</span>
                      </label>
                    </div>

                    {/* Year Input */}
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          value={currentImageQuestion.year}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, year: e.target.value }))}
                          placeholder="Year (e.g., 2024)"
                          className="text-center"
                        />
                      </div>
                      <select
                        value={currentImageQuestion.question_type}
                        onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, question_type: e.target.value }))}
                        className="p-2 rounded-xl border border-border bg-background text-foreground"
                      >
                        <option value="MCQ">MCQ</option>
                        <option value="Numerical">Numerical</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Optional Fields - Collapsible */}
                <details className="mt-4">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    ▸ Optional: Add text/explanation
                  </summary>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Question Text (optional)</label>
                      <textarea
                        value={currentImageQuestion.question_text}
                        onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, question_text: e.target.value }))}
                        placeholder="Additional text..."
                        className="w-full p-2 rounded-lg border border-border bg-background text-foreground text-sm min-h-[60px]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Explanation (optional)</label>
                      <textarea
                        value={currentImageQuestion.explanation}
                        onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, explanation: e.target.value }))}
                        placeholder="Explanation..."
                        className="w-full p-2 rounded-lg border border-border bg-background text-foreground text-sm min-h-[60px]"
                      />
                    </div>
                  </div>
                  {/* Option Text Fields */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                    {currentImageQuestion.options.map((opt, idx) => (
                      <Input
                        key={opt.label}
                        value={opt.text}
                        onChange={(e) => {
                          const newOptions = [...currentImageQuestion.options];
                          newOptions[idx].text = e.target.value;
                          setCurrentImageQuestion(prev => ({ ...prev, options: newOptions }));
                        }}
                        placeholder={`Option ${opt.label}`}
                        className="text-sm"
                      />
                    ))}
                  </div>
                </details>

                {/* Action Button - Prominent */}
                <div className="flex gap-3 mt-4 pt-4 border-t border-border">
                  <Button
                    onClick={async () => {
                      if (!currentImageQuestion.question_image) {
                        toast.error('Please upload a question image');
                        return;
                      }
                      
                      try {
                        setLoading(true);
                        const questionData = {
                          exam_id: selectedExam,
                          subject_id: selectedSubject,
                          chapter_id: selectedChapter,
                          question_text: currentImageQuestion.question_text || 'See image',
                          image_url: currentImageQuestion.question_image,
                          options: currentImageQuestion.options.map(o => ({ label: o.label, text: o.text || o.label })),
                          correct_answer: currentImageQuestion.correct_answer,
                          explanation: currentImageQuestion.explanation,
                          year: currentImageQuestion.year || null,
                          question_type: currentImageQuestion.question_type,
                          is_pyq: currentImageQuestion.is_pyq,
                          is_important: currentImageQuestion.is_important
                        };
                        
                        await api.post('/admin/questions', questionData);
                        toast.success(`✓ Question ${imageQuestions.length + 1} saved!`);
                        
                        // Add to batch list
                        setImageQuestions(prev => [...prev, { ...questionData, id: Date.now() }]);
                        
                        // Reset form but keep location & settings
                        setCurrentImageQuestion({
                          question_image: null,
                          question_text: '',
                          options: [
                            { label: 'A', text: '', image: null },
                            { label: 'B', text: '', image: null },
                            { label: 'C', text: '', image: null },
                            { label: 'D', text: '', image: null }
                          ],
                          correct_answer: 'A',
                          explanation: '',
                          solution_image: null,
                          year: currentImageQuestion.year,
                          is_pyq: currentImageQuestion.is_pyq,
                          is_important: currentImageQuestion.is_important,
                          question_type: 'MCQ'
                        });
                      } catch (err) {
                        toast.error('Failed to save question');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading || !currentImageQuestion.question_image}
                    className="flex-1 bg-green-600 hover:bg-green-700 h-12 text-lg"
                  >
                    {loading ? <Loader className="animate-spin mr-2" size={20} /> : <Check className="mr-2" size={20} />}
                    Save & Next
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentImageQuestion({
                        question_image: null,
                        question_text: '',
                        options: [
                          { label: 'A', text: '', image: null },
                          { label: 'B', text: '', image: null },
                          { label: 'C', text: '', image: null },
                          { label: 'D', text: '', image: null }
                        ],
                        correct_answer: 'A',
                        explanation: '',
                        solution_image: null,
                        year: '',
                        is_pyq: true,
                        is_important: false,
                        question_type: 'MCQ'
                      });
                    }}
                    className="h-12"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </Card>
            )}

            {/* Session Summary */}
            {imageQuestions.length > 0 && (
              <Card className="p-6 border-green-500/30 bg-green-500/5">
                <h4 className="font-semibold text-green-400 mb-4 flex items-center gap-2">
                  <Check size={20} />
                  Added This Session: {imageQuestions.length} questions
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {imageQuestions.slice(-12).map((q, idx) => (
                    <div key={q.id} className="relative group">
                      <img 
                        src={q.image_url.startsWith('/') ? `http://localhost:8000${q.image_url}` : q.image_url}
                        alt={`Q${idx + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-border"
                      />
                      <span className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                        Q{imageQuestions.length - 11 + idx}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Tips */}
            <Card className="p-4 border-amber-500/30 bg-amber-500/5">
              <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Info size={16} />
                Pro Tips for Fast Upload
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Windows:</strong> Use Snipping Tool (Win+Shift+S) to screenshot questions</li>
                <li>• <strong>Mac:</strong> Use Cmd+Shift+4 to capture question areas</li>
                <li>• Keep the PDF open and screenshot each question - much faster than manual typing</li>
                <li>• For batch upload: Screenshot multiple questions, then upload one by one here</li>
                <li>• Questions with diagrams/formulas will display exactly as captured</li>
              </ul>
            </Card>
          </div>
        )}

        {/* Scraper Tab */}
        {activeTab === 'scraper' && (
          <div className="space-y-6">
            {/* Header with Mode Toggle */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Download className="text-cyan-500" size={24} />
                  Web Question Scraper
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {bulkMode ? 'Scrape entire subjects with all chapters at once!' : 'Scrape questions from educational question banks and websites.'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={!bulkMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBulkMode(false)}
                  className={!bulkMode ? "bg-cyan-600" : ""}
                >
                  Single Chapter
                </Button>
                <Button
                  variant={bulkMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBulkMode(true)}
                  className={bulkMode ? "bg-orange-600" : ""}
                >
                  <Zap className="mr-1" size={14} />
                  Bulk Mode
                </Button>
              </div>
            </div>

            {/* ===== BULK MODE UI ===== */}
            {bulkMode && (
              <>
                {/* Step 1: Select Exam & Subject */}
                <Card className="p-6 border-orange-500/30 bg-orange-500/5">
                  <h4 className="font-semibold text-orange-400 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm">1</span>
                    Select Exam & Subject to Import To
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">Exam</label>
                      <select
                        value={selectedExam || ''}
                        onChange={(e) => {
                          setSelectedExam(e.target.value);
                          setSelectedSubject(null);
                          setChapters([]);
                          setAvailableChapters([]);
                          if (e.target.value) {
                            api.get(`/exams/${e.target.value}/subjects`).then(res => setSubjects(res.data || []));
                          }
                        }}
                        className="w-full p-3 rounded-xl border border-border bg-background text-foreground"
                      >
                        <option value="">Select Exam</option>
                        {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                      <select
                        value={selectedSubject || ''}
                        onChange={(e) => {
                          setSelectedSubject(e.target.value);
                          setAvailableChapters([]);
                          if (e.target.value) {
                            api.get(`/subjects/${e.target.value}/chapters`).then(res => setChapters(res.data || []));
                          }
                        }}
                        disabled={!selectedExam}
                        className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                      >
                        <option value="">Select Subject</option>
                        {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                </Card>

                {/* Step 2: Enter Subject URL and Discover Chapters */}
                {selectedSubject && (
                  <Card className="p-6 border-purple-500/30 bg-purple-500/5">
                    <h4 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">2</span>
                      Enter Subject Page URL & Discover Chapters
                    </h4>
                    
                    <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <p className="text-sm text-blue-400 font-medium mb-1">💡 Paste the SUBJECT page URL (not a chapter URL)</p>
                      <p className="text-xs text-muted-foreground">
                        Example: https://questions.examside.com/past-years/jee/jee-main/chemistry
                      </p>
                    </div>
                    
                    <div className="flex gap-4">
                      <Input
                        value={bulkSubjectUrl}
                        onChange={(e) => setBulkSubjectUrl(e.target.value)}
                        placeholder="https://questions.examside.com/past-years/jee/jee-main/chemistry"
                        className="flex-1"
                      />
                      <Button
                        onClick={async () => {
                          if (!bulkSubjectUrl) {
                            toast.error('Please enter a subject URL');
                            return;
                          }
                          setBulkLoading(true);
                          setAvailableChapters([]);
                          setSelectedBulkChapters([]);
                          setChapterMappings({});
                          
                          try {
                            // Extract exam and subject from URL
                            const urlMatch = bulkSubjectUrl.match(/past-years\/([^/]+)\/([^/]+)\/([^/]+)/);
                            if (!urlMatch) {
                              toast.error('Invalid URL format. Use: .../past-years/jee/jee-main/chemistry');
                              return;
                            }
                            
                            const [, , examSlug, subjectSlug] = urlMatch;
                            
                            const res = await api.get(`/admin/scrape/chapters?exam=${examSlug}&subject=${subjectSlug}`);
                            
                            if (res.data.chapters?.length > 0) {
                              setAvailableChapters(res.data.chapters);
                              
                              // Auto-map chapters to local chapters by fuzzy matching
                              const mappings = {};
                              res.data.chapters.forEach(ch => {
                                const localMatch = chapters.find(local => {
                                  const localName = local.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                                  const remoteName = ch.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                                  return localName === remoteName || 
                                         localName.includes(remoteName) || 
                                         remoteName.includes(localName);
                                });
                                if (localMatch) {
                                  mappings[ch.slug] = localMatch.id;
                                }
                              });
                              setChapterMappings(mappings);
                              
                              // Select all mapped chapters by default
                              setSelectedBulkChapters(Object.keys(mappings));
                              
                              toast.success(`Found ${res.data.chapters.length} chapters! ${Object.keys(mappings).length} auto-mapped.`);
                            } else {
                              toast.info('No chapters found on this URL');
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to discover chapters');
                          } finally {
                            setBulkLoading(false);
                          }
                        }}
                        disabled={bulkLoading}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {bulkLoading ? <Loader className="animate-spin mr-2" size={16} /> : <Search className="mr-2" size={16} />}
                        Discover Chapters
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Step 3: Select & Map Chapters */}
                {availableChapters.length > 0 && (
                  <Card className="p-6 border-green-500/30 bg-green-500/5">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <h4 className="font-semibold text-green-400 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">3</span>
                        Select Chapters to Import ({selectedBulkChapters.length}/{availableChapters.length})
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedBulkChapters(availableChapters.filter(ch => chapterMappings[ch.slug]).map(ch => ch.slug))}
                        >
                          Select Mapped
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedBulkChapters(availableChapters.map(ch => ch.slug))}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedBulkChapters([])}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto space-y-2">
                      {availableChapters.map((ch) => {
                        const isSelected = selectedBulkChapters.includes(ch.slug);
                        const mappedTo = chapterMappings[ch.slug];
                        const mappedChapter = chapters.find(c => c.id === mappedTo);
                        
                        return (
                          <div 
                            key={ch.slug}
                            className={`p-3 rounded-lg border transition-all ${
                              isSelected 
                                ? 'border-green-500 bg-green-500/10' 
                                : 'border-border bg-background/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedBulkChapters([...selectedBulkChapters, ch.slug]);
                                  } else {
                                    setSelectedBulkChapters(selectedBulkChapters.filter(s => s !== ch.slug));
                                  }
                                }}
                                className="w-5 h-5 rounded"
                              />
                              <div className="flex-1">
                                <p className="font-medium text-foreground">{ch.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{ch.url}</p>
                              </div>
                              <div className="min-w-[200px]">
                                <select
                                  value={mappedTo || ''}
                                  onChange={(e) => {
                                    setChapterMappings({ ...chapterMappings, [ch.slug]: e.target.value });
                                  }}
                                  className={`w-full p-2 text-sm rounded border ${
                                    mappedTo ? 'border-green-500 bg-green-500/10' : 'border-yellow-500 bg-yellow-500/10'
                                  }`}
                                >
                                  <option value="">-- Map to local chapter --</option>
                                  {chapters.map(local => (
                                    <option key={local.id} value={local.id}>{local.name}</option>
                                  ))}
                                </select>
                              </div>
                              {mappedTo && (
                                <Check className="text-green-500" size={20} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Step 4: Bulk Import */}
                {selectedBulkChapters.length > 0 && (
                  <Card className="p-6 border-cyan-500/30 bg-cyan-500/5">
                    <h4 className="font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-sm">4</span>
                      Start Bulk Import
                    </h4>
                    
                    <div className="mb-4 p-4 rounded-lg bg-background border border-border">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-green-500">{selectedBulkChapters.filter(s => chapterMappings[s]).length}</p>
                          <p className="text-xs text-muted-foreground">Chapters Ready</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-yellow-500">{selectedBulkChapters.filter(s => !chapterMappings[s]).length}</p>
                          <p className="text-xs text-muted-foreground">Not Mapped (Skip)</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-cyan-500">{bulkProgress.current}/{bulkProgress.total}</p>
                          <p className="text-xs text-muted-foreground">Progress</p>
                        </div>
                      </div>
                    </div>
                    
                    {bulkProgress.results.length > 0 && (
                      <div className="mb-4 max-h-[200px] overflow-y-auto space-y-1">
                        {bulkProgress.results.map((r, i) => (
                          <div key={i} className={`text-sm p-2 rounded ${r.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                            {r.success ? '✅' : '❌'} {r.chapter_name}: {r.success ? `${r.imported} questions imported` : r.error}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <Button
                      onClick={async () => {
                        const chaptersToImport = selectedBulkChapters
                          .filter(slug => chapterMappings[slug])
                          .map(slug => {
                            const ch = availableChapters.find(c => c.slug === slug);
                            return {
                              url: ch.url,
                              exam_id: selectedExam,
                              subject_id: selectedSubject,
                              chapter_id: chapterMappings[slug],
                              chapter_name: ch.name
                            };
                          });
                        
                        if (chaptersToImport.length === 0) {
                          toast.error('No chapters mapped. Please map at least one chapter.');
                          return;
                        }
                        
                        setBulkLoading(true);
                        setBulkProgress({ current: 0, total: chaptersToImport.length, results: [] });
                        
                        toast.loading(`Starting bulk import of ${chaptersToImport.length} chapters...`, { id: 'bulk-import' });
                        
                        try {
                          // Process chapters one by one for progress updates
                          const results = [];
                          for (let i = 0; i < chaptersToImport.length; i++) {
                            const ch = chaptersToImport[i];
                            setBulkProgress(prev => ({ ...prev, current: i + 1 }));
                            toast.loading(`Importing ${ch.chapter_name}... (${i + 1}/${chaptersToImport.length})`, { id: 'bulk-import' });
                            
                            try {
                              const res = await api.post('/admin/scrape/import', {
                                url: ch.url,
                                exam_id: ch.exam_id,
                                subject_id: ch.subject_id,
                                chapter_id: ch.chapter_id
                              });
                              results.push({
                                chapter_name: ch.chapter_name,
                                success: true,
                                scraped: res.data.scraped,
                                imported: res.data.imported
                              });
                            } catch (err) {
                              results.push({
                                chapter_name: ch.chapter_name,
                                success: false,
                                error: err.response?.data?.detail || 'Import failed'
                              });
                            }
                            
                            setBulkProgress(prev => ({ ...prev, results: [...results] }));
                            
                            // Small delay between chapters to not overwhelm server
                            await new Promise(r => setTimeout(r, 500));
                          }
                          
                          const successCount = results.filter(r => r.success).length;
                          const totalImported = results.filter(r => r.success).reduce((a, b) => a + b.imported, 0);
                          
                          toast.success(`Done! ${successCount}/${chaptersToImport.length} chapters, ${totalImported} questions imported!`, { id: 'bulk-import' });
                        } catch (err) {
                          toast.error('Bulk import failed', { id: 'bulk-import' });
                        } finally {
                          setBulkLoading(false);
                        }
                      }}
                      disabled={bulkLoading || selectedBulkChapters.filter(s => chapterMappings[s]).length === 0}
                      className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-lg py-6"
                    >
                      {bulkLoading ? (
                        <>
                          <Loader className="animate-spin mr-2" size={20} />
                          Importing... {bulkProgress.current}/{bulkProgress.total}
                        </>
                      ) : (
                        <>
                          <Zap className="mr-2" size={20} />
                          Import {selectedBulkChapters.filter(s => chapterMappings[s]).length} Chapters
                        </>
                      )}
                    </Button>
                  </Card>
                )}
              </>
            )}

            {/* ===== SINGLE CHAPTER MODE (Original) ===== */}
            {!bulkMode && (
              <>
            {/* Step 1: Select Location */}
            <Card className="p-6 border-cyan-500/30 bg-cyan-500/5">
              <h4 className="font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-sm">1</span>
                Select Target Location
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Exam</label>
                  <select
                    value={selectedExam || ''}
                    onChange={(e) => {
                      setSelectedExam(e.target.value);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/exams/${e.target.value}/subjects`).then(res => setSubjects(res.data || []));
                      }
                    }}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                  <select
                    value={selectedSubject || ''}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/subjects/${e.target.value}/chapters`).then(res => setChapters(res.data || []));
                      }
                    }}
                    disabled={!selectedExam}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Chapter</label>
                  <select
                    value={selectedChapter || ''}
                    onChange={(e) => setSelectedChapter(e.target.value)}
                    disabled={!selectedSubject}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </Card>

            {/* Step 2: Enter URL */}
            {selectedChapter && (
              <Card className="p-6 border-purple-500/30 bg-purple-500/5">
                <h4 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">2</span>
                  Enter Website URL
                </h4>
                
                {/* Quick Links */}
                <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-green-400 font-medium mb-2">✅ Pro Tip: Use chapter-specific URLs for best results (400+ PYQs per chapter!)</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Find your specific chapter page with questions, then copy the URL from browser address bar
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <Input
                    value={scraperUrl}
                    onChange={(e) => setScraperUrl(e.target.value)}
                    placeholder="https://example.com/questions/physics/chapter-name"
                    className="flex-1"
                  />
                  <Button
                    onClick={async () => {
                      if (!scraperUrl) {
                        toast.error('Please enter a URL');
                        return;
                      }
                      setScraperLoading(true);
                      setScrapedQuestions([]);
                      setFetchBatchProgress({ fetched: 0, total: 0, inProgress: false });
                      try {
                        const res = await api.post('/admin/scrape', { 
                          url: scraperUrl
                        });
                        setScrapedQuestions(res.data.questions || []);
                        if (res.data.questions?.length > 0) {
                          toast.success(`Found ${res.data.questions.length} questions via ${res.data.method}!`);
                        } else {
                          toast.info('No questions found. Please check the URL and try again.');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to scrape');
                      } finally {
                        setScraperLoading(false);
                      }
                    }}
                    disabled={scraperLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {scraperLoading ? <Loader className="animate-spin mr-2" size={16} /> : <Search className="mr-2" size={16} />}
                    Scrape
                  </Button>
                </div>
              </Card>
            )}

            {/* Step 3: Review & Import */}
            {scrapedQuestions.length > 0 && (
              <Card className="p-6 border-green-500/30 bg-green-500/5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h4 className="font-semibold text-green-400 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">3</span>
                    Review & Import ({scrapedQuestions.length} questions)
                  </h4>
                  <div className="flex gap-2">
                    {/* Fetch Options Batch Button */}
                    {(() => {
                      const needsFetch = scrapedQuestions.filter(q => q.detail_url && (!q.options?.length || !q.options[0]?.text));
                      const alreadyFetched = fetchBatchProgress.fetched;
                      const remaining = needsFetch.length;
                      const nextBatch = Math.min(50, remaining);
                      
                      if (remaining === 0) return null;
                      
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={fetchBatchProgress.inProgress}
                          onClick={async () => {
                            setFetchBatchProgress(prev => ({ ...prev, inProgress: true, total: remaining }));
                            toast.loading(`Fetching options for ${nextBatch} questions...`, { id: 'fetch-all' });
                            
                            let fetched = 0;
                            const updated = [...scrapedQuestions];
                            const toFetch = needsFetch.slice(0, 50);
                            
                            // eslint-disable-next-line no-loop-func
                            for (const q of toFetch) {
                              const idx = scrapedQuestions.findIndex(sq => sq.detail_url === q.detail_url);
                              try {
                                const res = await api.post('/admin/scrape/fetch-options', { detail_url: q.detail_url });
                                updated[idx].options = res.data.options;
                                updated[idx].correct_answer = res.data.correct_answer;
                                if (res.data.question_type) updated[idx].question_type = res.data.question_type;
                                fetched++;
                                
                                if (fetched % 5 === 0) {
                                  toast.loading(`Fetched ${fetched}/${nextBatch}...`, { id: 'fetch-all' });
                                  setFetchBatchProgress(prev => ({ ...prev, fetched: alreadyFetched + fetched }));
                                }
                                
                                await new Promise(r => setTimeout(r, 100));
                              } catch (e) {
                                console.error('Failed to fetch:', e);
                              }
                            }
                            
                            setScrapedQuestions(updated);
                            setFetchBatchProgress(prev => ({ ...prev, fetched: alreadyFetched + fetched, inProgress: false }));
                            
                            const newRemaining = remaining - fetched;
                            if (newRemaining > 0) {
                              toast.success(`Fetched ${fetched}! ${newRemaining} remaining - click Continue`, { id: 'fetch-all' });
                            } else {
                              toast.success(`All options fetched!`, { id: 'fetch-all' });
                            }
                          }}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          {fetchBatchProgress.inProgress ? (
                            <><Loader className="animate-spin mr-1" size={14} /> Fetching...</>
                          ) : alreadyFetched > 0 ? (
                            <><Download className="mr-1" size={14} /> Continue ({remaining} left)</>
                          ) : (
                            <><Download className="mr-1" size={14} /> Fetch Options ({nextBatch}/{remaining})</>
                          )}
                        </Button>
                      );
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setScrapedQuestions([])}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="mr-1" size={14} />
                      Clear All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Add a blank question for manual entry
                        setScrapedQuestions(prev => [...prev, {
                          question_text: '',
                          options: [
                            { label: 'A', text: '' },
                            { label: 'B', text: '' },
                            { label: 'C', text: '' },
                            { label: 'D', text: '' }
                          ],
                          correct_answer: 'A',
                          explanation: '',
                          year: '',
                          difficulty: 'medium',
                          is_pyq: false,
                          is_important: false,
                          image_url: '',
                          option_images: {},
                          solution_image: ''
                        }]);
                        toast.success('Added blank question for manual entry');
                      }}
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    >
                      <Plus className="mr-1" size={14} />
                      Add Manual Question
                    </Button>
                  </div>
                </div>

                {/* Preview Questions - Full Editable */}
                <div className="space-y-4 max-h-[500px] overflow-y-auto mb-4 pr-2">
                  {scrapedQuestions.map((q, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-border bg-background group relative">
                      {/* Action Buttons */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* Duplicate Button */}
                        <button
                          onClick={() => {
                            const duplicate = { ...q };
                            setScrapedQuestions(prev => [...prev.slice(0, idx + 1), duplicate, ...prev.slice(idx + 1)]);
                            toast.success('Question duplicated!');
                          }}
                          className="bg-blue-500/20 hover:bg-blue-500 text-blue-400 hover:text-white rounded-full p-1.5"
                          title="Duplicate this question"
                        >
                          <Copy size={14} />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => setScrapedQuestions(prev => prev.filter((_, i) => i !== idx))}
                          className="bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded-full p-1.5"
                          title="Remove this question"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        {/* Question Header with Fetch Options */}
                        <div className="flex items-start justify-between gap-2">
                          <label className="text-xs text-muted-foreground">Question {idx + 1} {q.year_info && <span className="text-green-400">• {q.year_info}</span>}</label>
                          {q.detail_url && (!q.options?.length || !q.options[0]?.text || (q.question_type === 'Numerical' && !q.correct_answer)) && (
                            <button
                              onClick={async () => {
                                try {
                                  toast.loading('Fetching details...', { id: `fetch-${idx}` });
                                  const res = await api.post('/admin/scrape/fetch-options', { detail_url: q.detail_url });
                                  const updated = [...scrapedQuestions];
                                  updated[idx].options = res.data.options;
                                  updated[idx].correct_answer = res.data.correct_answer;
                                  if (res.data.question_type) updated[idx].question_type = res.data.question_type;
                                  setScrapedQuestions(updated);
                                  toast.success(`${res.data.question_type === 'Numerical' ? 'Numerical answer' : 'Options'} fetched!`, { id: `fetch-${idx}` });
                                } catch (err) {
                                  toast.error('Failed to fetch options', { id: `fetch-${idx}` });
                                }
                              }}
                              className="text-xs px-2 py-1 bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 rounded transition-colors"
                            >
                              🔍 Fetch Details
                            </button>
                          )}
                        </div>
                        
                        {/* Question Text - Rendered or Editable for manual */}
                        {!q.question_text ? (
                          <textarea
                            value={q.question_text || ''}
                            onChange={(e) => {
                              const updated = [...scrapedQuestions];
                              updated[idx].question_text = e.target.value;
                              setScrapedQuestions(updated);
                            }}
                            placeholder="Enter your question text here... (supports LaTeX with $...$)"
                            className="w-full p-3 rounded-lg border border-border bg-background text-sm min-h-[80px]"
                          />
                        ) : (
                          <>
                            <div 
                              className="p-2 rounded-lg bg-muted/20 text-sm text-foreground prose prose-sm dark:prose-invert max-w-none"
                              dangerouslySetInnerHTML={{ 
                                __html: renderLatex(q.question_text)
                              }}
                            />
                            {/* Editable Question (collapsed by default) */}
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">✏️ Edit raw text</summary>
                              <textarea
                                value={q.question_text}
                                onChange={(e) => {
                                  const updated = [...scrapedQuestions];
                                  updated[idx].question_text = e.target.value;
                                  setScrapedQuestions(updated);
                                }}
                                className="w-full p-2 rounded-lg border border-border bg-muted/30 text-foreground text-xs min-h-[60px] mt-2"
                              />
                            </details>
                          </>
                        )}
                        
                        {/* Image Required Warning */}
                        {(q.question_text?.toLowerCase().includes('figure') || 
                          q.question_text?.toLowerCase().includes('diagram') || 
                          q.question_text?.toLowerCase().includes('shown below') ||
                          q.question_text?.toLowerCase().includes('given figure') ||
                          q.question_text?.includes('[IMAGE REQUIRED]')) && !q.image_url && (
                          <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                            <Image size={12} />
                            <span>⚠️ This question may require an image/diagram - add one below!</span>
                          </div>
                        )}
                        
                        {/* Editable Options (MCQ) or Answer (Numerical) */}
                        {q.question_type === 'Numerical' ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Correct Answer:</span>
                            <input
                              type="text"
                              value={q.correct_answer || ''}
                              onChange={(e) => {
                                const updated = [...scrapedQuestions];
                                updated[idx].correct_answer = e.target.value;
                                setScrapedQuestions(updated);
                              }}
                              placeholder="Enter numerical answer (e.g., 42, -3.14)"
                              className="flex-1 max-w-xs p-2 rounded border border-green-500/50 bg-green-500/10 text-sm font-mono"
                            />
                          </div>
                        ) : (
                          <>
                            {/* Rendered Options */}
                            <div className="grid grid-cols-2 gap-2">
                              {(q.options?.length ? q.options : [{label:'A',text:''},{label:'B',text:''},{label:'C',text:''},{label:'D',text:''}]).map((opt, optIdx) => (
                                <div key={opt.label} className={`flex items-center gap-2 p-2 rounded border text-sm ${
                                  opt.label === q.correct_answer 
                                    ? 'border-green-500/50 bg-green-500/10' 
                                    : 'border-border bg-muted/20'
                                }`}>
                                  <button
                                    onClick={() => {
                                      const updated = [...scrapedQuestions];
                                      updated[idx].correct_answer = opt.label;
                                      setScrapedQuestions(updated);
                                    }}
                                    className={`w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                                      opt.label === q.correct_answer 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-muted text-muted-foreground hover:bg-green-500/30'
                                    }`}
                                    title={opt.label === q.correct_answer ? 'Correct answer' : 'Click to set as correct'}
                                  >
                                    {opt.label}
                                  </button>
                                  <span className="flex-1 text-xs text-foreground" dangerouslySetInnerHTML={{ __html: renderLatex(opt.text || `Option ${opt.label}`) }} />
                                </div>
                              ))}
                            </div>
                            {/* Edit Options (collapsed) */}
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">✏️ Edit options</summary>
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                {(q.options?.length ? q.options : [{label:'A',text:''},{label:'B',text:''},{label:'C',text:''},{label:'D',text:''}]).map((opt, optIdx) => (
                                  <div key={opt.label} className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-muted-foreground w-4">{opt.label}</span>
                                    <input
                                      value={opt.text || ''}
                                      onChange={(e) => {
                                        const updated = [...scrapedQuestions];
                                        if (!updated[idx].options) {
                                          updated[idx].options = [{label:'A',text:''},{label:'B',text:''},{label:'C',text:''},{label:'D',text:''}];
                                        }
                                        updated[idx].options[optIdx].text = e.target.value;
                                        setScrapedQuestions(updated);
                                      }}
                                      placeholder={`Option ${opt.label}`}
                                      className="flex-1 p-1.5 rounded border border-border bg-background text-xs"
                                    />
                                  </div>
                                ))}
                              </div>
                            </details>
                          </>
                        )}
                        
                        {/* Extra Details Row */}
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          {/* Question Type */}
                          <select
                            value={q.question_type || 'MCQ'}
                            onChange={(e) => {
                              const updated = [...scrapedQuestions];
                              updated[idx].question_type = e.target.value;
                              setScrapedQuestions(updated);
                            }}
                            className="p-1.5 rounded border border-border bg-background text-foreground"
                          >
                            <option value="MCQ">MCQ</option>
                            <option value="Numerical">Numerical</option>
                          </select>
                          <input
                            type="text"
                            value={q.year || ''}
                            onChange={(e) => {
                              const updated = [...scrapedQuestions];
                              updated[idx].year = e.target.value;
                              setScrapedQuestions(updated);
                            }}
                            placeholder="Year"
                            className="w-20 p-1.5 rounded border border-border bg-background"
                          />
                          <select
                            value={q.difficulty || 'medium'}
                            onChange={(e) => {
                              const updated = [...scrapedQuestions];
                              updated[idx].difficulty = e.target.value;
                              setScrapedQuestions(updated);
                            }}
                            className="p-1.5 rounded border border-border bg-background text-foreground"
                          >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                          </select>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={q.is_pyq || false}
                              onChange={(e) => {
                                const updated = [...scrapedQuestions];
                                updated[idx].is_pyq = e.target.checked;
                                setScrapedQuestions(updated);
                              }}
                              className="w-3.5 h-3.5 accent-blue-500"
                            />
                            <span className="text-blue-400">PYQ</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={q.is_important || false}
                              onChange={(e) => {
                                const updated = [...scrapedQuestions];
                                updated[idx].is_important = e.target.checked;
                                setScrapedQuestions(updated);
                              }}
                              className="w-3.5 h-3.5 accent-amber-500"
                            />
                            <span className="text-amber-400">⭐</span>
                          </label>
                        </div>
                        
                        {/* Tags Row */}
                        <div className="text-xs">
                          <input
                            type="text"
                            value={Array.isArray(q.tags) ? q.tags.join(', ') : (q.tags || '')}
                            onChange={(e) => {
                              const updated = [...scrapedQuestions];
                              updated[idx].tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                              setScrapedQuestions(updated);
                            }}
                            placeholder="Tags (comma separated): kinematics, mechanics, easy..."
                            className="w-full p-1.5 rounded border border-border bg-background"
                          />
                        </div>
                        
                        {/* === IMAGE & EXPLANATION SECTION === */}
                        <details className="mt-2 text-xs border-t border-border pt-3">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground flex items-center gap-2">
                            <Image size={12} />
                            <span>Images & Explanation</span>
                            {(q.image_url || q.solution_image) && <span className="text-green-400">✓</span>}
                          </summary>
                          
                          <div className="mt-3 space-y-3">
                            {/* Question Image */}
                            <div>
                              <label className="text-muted-foreground mb-1 block">
                                Question Image &nbsp;<kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+V</kbd> to paste
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={q.image_url || ''}
                                  onChange={(e) => {
                                    const updated = [...scrapedQuestions];
                                    updated[idx].image_url = e.target.value;
                                    updated[idx].image_key = undefined;
                                    setScrapedQuestions(updated);
                                  }}
                                  onPaste={async (e) => {
                                    const items = e.clipboardData?.items;
                                    if (!items) return;
                                    for (const item of Array.from(items)) {
                                      if (item.type.startsWith('image/')) {
                                        e.preventDefault();
                                        const file = item.getAsFile();
                                        if (!file) return;
                                        const toastId = `paste-img-${idx}`;
                                        try {
                                          toast.loading('Compressing…', { id: toastId });
                                          const compressed = await compressImage(file);
                                          const mimeToExt = { 'image/jpeg':'jpg','image/png':'png','image/webp':'webp','image/gif':'gif' };
                                          const ext = mimeToExt[compressed.type] || mimeToExt[file.type] || 'png';
                                          const safeName = /\.(jpe?g|png|webp|gif)$/i.test(compressed.name||'') ? compressed.name : `image.${ext}`;
                                          const qId = q._pending_id || crypto.randomUUID();
                                          const fd = new FormData();
                                          fd.append('file', compressed, safeName);
                                          fd.append('exam_id', selectedExam || 'general');
                                          fd.append('subject_id', selectedSubject || 'general');
                                          fd.append('chapter_id', selectedChapter || 'general');
                                          fd.append('question_id', qId);
                                          fd.append('image_type', 'question');
                                          toast.loading('Uploading…', { id: toastId });
                                          const res = await api.post('/admin/upload/question-image', fd, {
                                            headers: { 'Content-Type': 'multipart/form-data' }
                                          });
                                          const updated = [...scrapedQuestions];
                                          updated[idx].image_url = res.data.url;
                                          updated[idx].image_key = res.data.key;
                                          updated[idx]._pending_id = qId;
                                          setScrapedQuestions(updated);
                                          toast.success('Image pasted!', { id: toastId });
                                        } catch {
                                          toast.error('Upload failed', { id: toastId });
                                        }
                                        return;
                                      }
                                    }
                                  }}
                                  placeholder="https://… or Ctrl+V to paste image"
                                  className="flex-1 p-1.5 rounded border border-border bg-background"
                                />
                                <label className="px-2 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded cursor-pointer transition-colors flex items-center gap-1">
                                  <Upload size={12} />
                                  Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const toastId = `file-img-${idx}`;
                                      try {
                                        toast.loading('Compressing…', { id: toastId });
                                        const compressed = await compressImage(file);
                                        const mimeToExt = { 'image/jpeg':'jpg','image/png':'png','image/webp':'webp','image/gif':'gif' };
                                        const ext = mimeToExt[compressed.type] || mimeToExt[file.type] || 'png';
                                        const safeName = /\.(jpe?g|png|webp|gif)$/i.test(compressed.name||'') ? compressed.name : `image.${ext}`;
                                        const qId = q._pending_id || crypto.randomUUID();
                                        const fd = new FormData();
                                        fd.append('file', compressed, safeName);
                                        fd.append('exam_id', selectedExam || 'general');
                                        fd.append('subject_id', selectedSubject || 'general');
                                        fd.append('chapter_id', selectedChapter || 'general');
                                        fd.append('question_id', qId);
                                        fd.append('image_type', 'question');
                                        toast.loading('Uploading…', { id: toastId });
                                        const res = await api.post('/admin/upload/question-image', fd, {
                                          headers: { 'Content-Type': 'multipart/form-data' }
                                        });
                                        const updated = [...scrapedQuestions];
                                        updated[idx].image_url = res.data.url;
                                        updated[idx].image_key = res.data.key;
                                        updated[idx]._pending_id = qId;
                                        setScrapedQuestions(updated);
                                        toast.success('Image uploaded!', { id: toastId });
                                      } catch {
                                        toast.error('Upload failed', { id: toastId });
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                              {q.image_url && (
                                <img src={q.image_url} alt="Question" className="mt-2 max-h-24 rounded border" />
                              )}
                            </div>
                            
                            {/* Option Images */}
                            <div>
                              <label className="text-muted-foreground mb-1 block">Option Images (for diagram-based options)</label>
                              <div className="grid grid-cols-2 gap-2">
                                {['A', 'B', 'C', 'D'].map(label => (
                                  <div key={label} className="flex items-center gap-1">
                                    <span className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs font-bold">{label}</span>
                                    <input
                                      type="text"
                                      value={q.option_images?.[label] || ''}
                                      onChange={(e) => {
                                        const updated = [...scrapedQuestions];
                                        if (!updated[idx].option_images) updated[idx].option_images = {};
                                        updated[idx].option_images[label] = e.target.value;
                                        setScrapedQuestions(updated);
                                      }}
                                      placeholder={`Option ${label} image URL`}
                                      className="flex-1 p-1 rounded border border-border bg-background text-xs"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Solution Image */}
                            <div>
                              <label className="text-muted-foreground mb-1 block">Solution Image URL</label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={q.solution_image || ''}
                                  onChange={(e) => {
                                    const updated = [...scrapedQuestions];
                                    updated[idx].solution_image = e.target.value;
                                    setScrapedQuestions(updated);
                                  }}
                                  placeholder="Solution diagram/handwritten solution URL"
                                  className="flex-1 p-1.5 rounded border border-border bg-background"
                                />
                                <label className="px-2 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded cursor-pointer transition-colors flex items-center gap-1">
                                  <Upload size={12} />
                                  Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      try {
                                        toast.loading('Compressing...', { id: 'sol-img-upload' });
                                        const compressedFile = await compressImage(file);
                                        const formData = new FormData();
                                        formData.append('file', compressedFile);
                                        formData.append('category', 'solutions');
                                        toast.loading('Uploading...', { id: 'sol-img-upload' });
                                        const res = await api.post('/admin/upload/image', formData, {
                                          headers: { 'Content-Type': 'multipart/form-data' }
                                        });
                                        const updated = [...scrapedQuestions];
                                        updated[idx].solution_image = res.data.url;
                                        setScrapedQuestions(updated);
                                        toast.success('Solution image uploaded!', { id: 'sol-img-upload' });
                                      } catch (err) {
                                        toast.error('Upload failed', { id: 'sol-img-upload' });
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                              {q.solution_image && (
                                <img src={q.solution_image} alt="Solution" className="mt-2 max-h-24 rounded border" />
                              )}
                            </div>
                            
                            {/* Explanation Text */}
                            <div>
                              <label className="text-muted-foreground mb-1 block">Explanation</label>
                              <textarea
                                value={q.explanation || ''}
                                onChange={(e) => {
                                  const updated = [...scrapedQuestions];
                                  updated[idx].explanation = e.target.value;
                                  setScrapedQuestions(updated);
                                }}
                                placeholder="Enter solution explanation..."
                                className="w-full p-2 rounded border border-border bg-background min-h-[60px]"
                              />
                            </div>
                          </div>
                        </details>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bulk Actions & Import */}
                <div className="flex items-center gap-4 pt-4 border-t border-border flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setScrapedQuestions(prev => prev.map(q => ({ ...q, is_important: checked })));
                      }}
                      className="w-4 h-4 accent-amber-500" 
                    />
                    <span className="text-sm text-amber-400">⭐ Mark All Important</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setScrapedQuestions(prev => prev.map(q => ({ ...q, is_pyq: checked })));
                      }}
                      className="w-4 h-4 accent-blue-500" 
                    />
                    <span className="text-sm text-blue-400">📜 Mark All PYQ</span>
                  </label>
                  <div className="flex-1" />
                  <Button
                    onClick={async () => {
                      setLoading(true);
                      try {
                        // Import questions directly from the edited list
                        let imported = 0;
                        for (const q of scrapedQuestions) {
                          try {
                            await api.post('/admin/questions', {
                              exam_id: selectedExam,
                              subject_id: selectedSubject,
                              chapter_id: selectedChapter,
                              question_text: q.question_text,
                              question_type: q.question_type || 'MCQ',
                              options: q.options,
                              correct_answer: q.correct_answer,
                              explanation: q.explanation || '',
                              year: q.year || null,
                              difficulty: q.difficulty || 'medium',
                              is_pyq: q.is_pyq || false,
                              is_important: q.is_important || false,
                              tags: Array.isArray(q.tags) ? q.tags : (q.tags || '').split(',').map(t => t.trim()).filter(Boolean),
                              image_url: q.image_url || null,
                              image_key: q.image_key || null,
                              option_images: q.option_images || null,
                              solution_image: q.solution_image || null,
                              ...(q._pending_id ? { id: q._pending_id } : {})
                            });
                            imported++;
                          } catch (e) {
                            console.error('Failed to import question:', e);
                          }
                        }
                        toast.success(`Imported ${imported} questions!`);
                        setScrapedQuestions([]);
                        setScraperUrl('');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Import failed');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading || scrapedQuestions.length === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {loading ? <Loader className="animate-spin mr-2" size={16} /> : <Check className="mr-2" size={16} />}
                    Import {scrapedQuestions.length} Questions
                  </Button>
                </div>
              </Card>
            )}
              </>
            )}

            {/* Reality Check & Tips */}
            <Card className="p-4 border-amber-500/30 bg-amber-500/5">
              <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Info size={16} />
                Scraping Tips
              </h4>
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong>✅ Best Results:</strong> Use chapter-specific URLs from question banks. Get 400+ PYQs per chapter with year info.</p>
                <p><strong>⚠️ Note:</strong> Some sites may require manual option entry. Use "Fetch Options" button for supported sources.</p>
                <p><strong>Other sites:</strong> Most modern sites (Embibe, Toppr) block scrapers. Use 📸 Image Q for those.</p>
              </div>
            </Card>
          </div>
        )}

        {/* PDF Upload Tab */}
        {activeTab === 'pdf-upload' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <File className="text-red-500" size={24} />
                  Bulk Import Questions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Import via CSV (fastest), JSON, or PDF
                </p>
              </div>
              <a 
                href="http://localhost:8000/api/admin/questions/csv-template"
                className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-green-600"
              >
                <Download size={16} />
                Download CSV Template
              </a>
            </div>

            {/* Selection & Options */}
            <Card className="p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
              <p className="text-sm font-medium mb-3">Select where to import:</p>
              <div className="flex gap-4 flex-wrap items-end">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Exam *</label>
                  <select
                    value={selectedExam || ''}
                    onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                    className="p-2 rounded-lg bg-background border border-border text-sm min-w-[180px]"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Subject *</label>
                  <select
                    value={selectedSubject || ''}
                    onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                    className="p-2 rounded-lg bg-background border border-border text-sm min-w-[180px]"
                    disabled={!selectedExam}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Chapter *</label>
                  <select
                    value={selectedChapter || ''}
                    onChange={(e) => setSelectedChapter(e.target.value)}
                    className="p-2 rounded-lg bg-background border border-border text-sm min-w-[180px]"
                    disabled={!selectedSubject}
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2 pb-1">
                  <input
                    type="checkbox"
                    checked={autoGenerateSolutions}
                    onChange={(e) => setAutoGenerateSolutions(e.target.checked)}
                    className="w-4 h-4"
                    id="autoSolutions"
                  />
                  <label htmlFor="autoSolutions" className="text-sm">Auto-generate solutions</label>
                </div>
              </div>
            </Card>

            {/* CSV/JSON Upload Zone - FASTEST METHOD */}
            <Card className="p-6 border-2 border-green-500/50 bg-green-500/5">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Zap className="text-green-500" size={20} />
                CSV/JSON Import (Fastest - No AI Limits!)
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Import prepared questions instantly. No AI rate limits, no delays.
                Download the template above, fill it in Excel, and upload!
              </p>
              
              <label className="block cursor-pointer">
                <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  csvFile ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-green-500 hover:bg-green-500/5'
                }`}>
                  {csvFile ? (
                    <>
                      <Check className="mx-auto text-green-500 mb-2" size={40} />
                      <p className="text-sm font-medium text-foreground">{csvFile.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(csvFile.size / 1024).toFixed(2)} KB - Ready to import
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="mx-auto text-green-500 mb-2" size={40} />
                      <p className="text-sm font-medium text-foreground">Click to upload CSV or JSON</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Prepared question files (CSV from Excel, or JSON array)
                      </p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  accept=".csv,.json" 
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="hidden" 
                />
              </label>

              {csvFile && (
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={async () => {
                      if (!selectedExam || !selectedSubject || !selectedChapter) {
                        toast.error('Please select Exam, Subject and Chapter');
                        return;
                      }
                      
                      setCsvUploading(true);
                      setCsvImportResult(null);
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', csvFile);
                        formData.append('exam_id', selectedExam);
                        formData.append('subject_id', selectedSubject);
                        formData.append('chapter_id', selectedChapter);
                        
                        const res = await api.post('/admin/questions/import-csv', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        setCsvImportResult(res.data);
                        toast.success(`Imported ${res.data.imported} questions!`);
                        loadStats();
                      } catch (error) {
                        toast.error('Failed: ' + (error.response?.data?.detail || error.message));
                        setCsvImportResult({ error: error.response?.data?.detail || error.message });
                      } finally {
                        setCsvUploading(false);
                      }
                    }}
                    disabled={csvUploading || !selectedExam || !selectedSubject || !selectedChapter}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  >
                    {csvUploading ? (
                      <>
                        <Loader size={16} className="animate-spin mr-2" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Zap size={16} className="mr-2" />
                        Import Questions Instantly
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => { setCsvFile(null); setCsvImportResult(null); }}>
                    <X size={16} />
                  </Button>
                </div>
              )}

              {/* Import Result */}
              {csvImportResult && !csvImportResult.error && (
                <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <Check size={20} />
                    <span className="font-bold">Successfully imported {csvImportResult.imported} questions!</span>
                  </div>
                  {csvImportResult.skipped > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Skipped {csvImportResult.skipped} questions (duplicates or invalid)
                    </p>
                  )}
                </div>
              )}
              {csvImportResult?.error && (
                <div className="mt-4 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <X size={20} />
                    <span className="font-medium">{csvImportResult.error}</span>
                  </div>
                </div>
              )}
            </Card>

            {/* PDF Upload Zone (AI-based) */}
            <Card className="p-6 border border-border">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Upload className="text-red-500" size={20} />
                Upload PDF File (AI-Powered)
              </h4>
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                ⚠️ Uses AI - may hit rate limits with large files. Use CSV above for bulk imports!
              </p>
              
              <label className="block cursor-pointer">
                <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  pdfFile ? 'border-green-500 bg-green-500/5' : 'border-border hover:border-red-500 hover:bg-red-500/5'
                }`}>
                  {pdfFile ? (
                    <>
                      <Check className="mx-auto text-green-500 mb-2" size={40} />
                      <p className="text-sm font-medium text-foreground">{pdfFile.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(pdfFile.size / 1024 / 1024).toFixed(2)} MB - Ready to process
                      </p>
                    </>
                  ) : (
                    <>
                      <File className="mx-auto text-muted-foreground mb-2" size={40} />
                      <p className="text-sm font-medium text-foreground">Click to upload PDF</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JEE/NEET question bank PDFs work best
                      </p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  accept=".pdf" 
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  className="hidden" 
                />
              </label>

              {pdfFile && (
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={async () => {
                      if (!selectedExam || !selectedSubject || !selectedChapter) {
                        toast.error('Please select Exam, Subject and Chapter');
                        return;
                      }
                      
                      setPdfUploading(true);
                      setPdfProgress({ status: 'Uploading PDF...', page: 0, total: 0 });
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', pdfFile);
                        formData.append('exam_id', selectedExam);
                        formData.append('subject_id', selectedSubject);
                        formData.append('chapter_id', selectedChapter);
                        formData.append('auto_generate_solutions', autoGenerateSolutions);
                        
                        const res = await api.post('/admin/upload/pdf', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        setPdfExtractedQuestions(res.data.questions || []);
                        setPdfProgress({ 
                          status: 'Complete!', 
                          page: res.data.total_pages, 
                          total: res.data.total_pages 
                        });
                        
                        toast.success(`Extracted ${res.data.questions_extracted} questions from ${res.data.total_pages} pages!`);
                        loadStats();
                      } catch (error) {
                        toast.error('Failed: ' + (error.response?.data?.detail || error.message));
                        setPdfProgress({ status: 'Failed', page: 0, total: 0 });
                      } finally {
                        setPdfUploading(false);
                      }
                    }}
                    disabled={pdfUploading || !selectedExam || !selectedSubject || !selectedChapter}
                    className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                  >
                    {pdfUploading ? (
                      <>
                        <Loader size={16} className="animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap size={16} className="mr-2" />
                        Extract Questions with AI
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => { setPdfFile(null); setPdfExtractedQuestions([]); }}>
                    <X size={16} />
                  </Button>
                </div>
              )}

              {/* Progress */}
              {pdfUploading && (
                <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader className="animate-spin text-blue-500" size={16} />
                    <span className="text-sm font-medium">{pdfProgress.status}</span>
                  </div>
                  {pdfProgress.total > 0 && (
                    <>
                      <div className="w-full h-2 bg-blue-200 dark:bg-blue-900 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${(pdfProgress.page / pdfProgress.total) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Page {pdfProgress.page} of {pdfProgress.total}
                      </p>
                    </>
                  )}
                </div>
              )}
            </Card>

            {/* Extracted Questions Preview */}
            {pdfExtractedQuestions.length > 0 && (
              <Card className="p-6 border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-foreground flex items-center gap-2">
                    <Check className="text-green-500" size={20} />
                    Extracted {pdfExtractedQuestions.length} Questions
                  </h4>
                  <Button variant="outline" onClick={() => setPdfExtractedQuestions([])}>
                    Clear
                  </Button>
                </div>

                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {pdfExtractedQuestions.map((q, i) => (
                    <div key={i} className="p-4 bg-background rounded-xl border border-border">
                      <div className="flex items-start gap-3">
                        <span className="text-xs font-bold text-white bg-purple-500 px-2 py-1 rounded">
                          Q{i + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-foreground mb-2">
                            {q.question_text?.substring(0, 200)}{q.question_text?.length > 200 ? '...' : ''}
                          </p>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            {q.options?.map((opt, j) => (
                              <div 
                                key={j} 
                                className={`text-xs p-2 rounded border ${
                                  opt.label === q.correct_answer 
                                    ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-400' 
                                    : 'border-border'
                                }`}
                              >
                                <strong>{opt.label})</strong> <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text || '') }} />
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Badge className="bg-green-500/20 text-green-600 text-xs">
                              Answer: {q.correct_answer}
                            </Badge>
                            {q.has_diagram && (
                              <Badge className="bg-amber-500/20 text-amber-600 text-xs">
                                Has Diagram
                              </Badge>
                            )}
                            <Badge className="bg-gray-500/20 text-gray-600 text-xs">
                              {q.difficulty || 'medium'}
                            </Badge>
                            {q.image_url && (
                              <Badge className="bg-blue-500/20 text-blue-600 text-xs">
                                Image Saved
                              </Badge>
                            )}
                          </div>
                          {q.explanation && (
                            <details className="mt-2">
                              <summary className="text-xs text-primary cursor-pointer">View Solution</summary>
                              <div className="mt-2 p-2 bg-muted/50 rounded text-xs whitespace-pre-wrap">
                                {q.explanation.substring(0, 500)}...
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Instructions */}
            <Card className="p-6 border border-border bg-gradient-to-r from-red-500/5 to-orange-500/5">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Info className="text-red-500" size={20} />
                How PDF Upload Works
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 bg-background rounded-lg border border-border">
                  <div className="text-2xl mb-2">1️⃣</div>
                  <strong className="text-foreground">Upload PDF</strong>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select your JEE/NEET question bank PDF (works with Mathongo, PW, etc.)
                  </p>
                </div>
                <div className="p-4 bg-background rounded-lg border border-border">
                  <div className="text-2xl mb-2">2️⃣</div>
                  <strong className="text-foreground">AI Extracts</strong>
                  <p className="text-xs text-muted-foreground mt-1">
                    AI reads each page, extracts questions, options, and ignores watermarks
                  </p>
                </div>
                <div className="p-4 bg-background rounded-lg border border-border">
                  <div className="text-2xl mb-2">3️⃣</div>
                  <strong className="text-foreground">Solutions Generated</strong>
                  <p className="text-xs text-muted-foreground mt-1">
                    Groq AI generates detailed step-by-step solutions for each question
                  </p>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>⚡ Pro Tip:</strong> Images from PDF pages are automatically saved to Cloudflare R2 (if configured) or locally. 
                  Questions with diagrams will have the image attached automatically!
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Bulk Import Tab */}
        {activeTab === 'bulk-import' && (
          <div className="space-y-6">
            {/* Header with actions */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Upload className="text-purple-500" size={24} />
                  Bulk Import Questions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Import questions from JSON files with images support
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download size={16} className="mr-2" /> Download Template
              </Button>
            </div>

            {/* Selection Bar */}
            <Card className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
              <p className="text-sm font-medium mb-3">Select where to import:</p>
              <div className="flex gap-4 flex-wrap">
                <select
                  value={selectedExam || ''}
                  onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                >
                  <option value="">Select Exam *</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <select
                  value={selectedSubject || ''}
                  onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedExam}
                >
                  <option value="">Select Subject (optional)</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  value={selectedChapter || ''}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedSubject}
                >
                  <option value="">Select Chapter (optional)</option>
                  {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: JSON Input */}
              <Card className="p-6 border border-border">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <FileJson className="text-green-500" size={20} />
                  JSON Questions
                </h4>
                
                {/* File Upload */}
                <div className="mb-4">
                  <label className="block">
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                      <FileJson className="mx-auto text-muted-foreground mb-2" size={32} />
                      <p className="text-sm font-medium text-foreground">Upload JSON File</p>
                      <p className="text-xs text-muted-foreground mt-1">Click or drag & drop</p>
                    </div>
                    <input type="file" accept=".json" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>

                {/* Manual JSON Input */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-foreground mb-2 block">Or paste JSON directly:</label>
                  <textarea
                    value={bulkImportData}
                    onChange={(e) => setBulkImportData(e.target.value)}
                    className="w-full p-3 rounded-lg bg-background border border-border text-sm font-mono min-h-[200px] focus:ring-2 focus:ring-primary"
                    placeholder='[{"question_text": "...", "options": [...], "correct_answer": "A", ...}]'
                  />
                </div>

                <Button onClick={handleJsonParse} className="w-full" disabled={!bulkImportData}>
                  <Eye size={16} className="mr-2" /> Parse & Preview
                </Button>
              </Card>

              {/* Right: Image Upload */}
              <Card className="p-6 border border-border">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Image className="text-amber-500" size={20} />
                  Upload Images
                </h4>

                {/* Image Upload */}
                <div className="mb-4">
                  <label className="block">
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                      <Image className="mx-auto text-muted-foreground mb-2" size={32} />
                      <p className="text-sm font-medium text-foreground">Upload Images</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF, WEBP (multiple)</p>
                    </div>
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>

                {/* Upload Progress */}
                {importProgress.uploading && (
                  <div className="mb-4 p-3 bg-blue-500/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Loader className="animate-spin text-blue-500" size={16} />
                      <span className="text-sm">Uploading {importProgress.current}/{importProgress.total}...</span>
                    </div>
                    <div className="w-full h-2 bg-blue-200 rounded-full mt-2 overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Uploaded Images List */}
                {uploadedImages.length > 0 && (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    <p className="text-sm font-medium text-foreground">Uploaded ({uploadedImages.length}):</p>
                    {uploadedImages.map((img, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-green-500/10 rounded-lg">
                        <span className="truncate flex-1">{img.original}</span>
                        <code className="ml-2 px-2 py-1 bg-background rounded text-green-600">{img.url}</code>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Tip:</strong> After uploading images, copy the URL and use it in your JSON as <code>image_url</code>, <code>option_images</code>, or <code>solution_image</code>.
                  </p>
                </div>
              </Card>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <Card className="p-6 border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-foreground flex items-center gap-2">
                    <Eye className="text-blue-500" size={20} />
                    Preview ({importPreview.length} questions)
                  </h4>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportPreview([])}>
                      <X size={16} className="mr-2" /> Clear
                    </Button>
                    <Button onClick={handleBulkImport} disabled={loading || !selectedExam} className="bg-green-500 hover:bg-green-600">
                      {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
                      Import {importPreview.length} Questions
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {importPreview.slice(0, 10).map((q, i) => (
                    <div key={i} className="p-4 bg-background rounded-xl border border-border">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-foreground mb-2">
                            {i + 1}. {q.question_text?.substring(0, 150)}{q.question_text?.length > 150 ? '...' : ''}
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            {q.image_url && <Badge className="bg-amber-500/20 text-amber-600 text-xs">Has Image</Badge>}
                            {q.is_pyq && <Badge className="bg-blue-500/20 text-blue-600 text-xs">PYQ</Badge>}
                            {q.is_important && <Badge className="bg-red-500/20 text-red-600 text-xs">Important</Badge>}
                            <Badge className="bg-gray-500/20 text-gray-600 text-xs">{q.difficulty || 'medium'}</Badge>
                            <Badge className="bg-green-500/20 text-green-600 text-xs">Answer: {q.correct_answer}</Badge>
                            {q.year && <Badge className="bg-purple-500/20 text-purple-600 text-xs">{q.year}</Badge>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {importPreview.length > 10 && (
                    <p className="text-center text-muted-foreground text-sm">
                      ... and {importPreview.length - 10} more questions
                    </p>
                  )}
                </div>
              </Card>
            )}

            {/* Instructions */}
            <Card className="p-6 border border-border bg-gradient-to-r from-indigo-500/5 to-purple-500/5">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Info className="text-indigo-500" size={20} />
                How to Bulk Import Efficiently
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div className="p-3 bg-background rounded-lg">
                    <strong className="text-green-500">Step 1:</strong> Upload all images first
                    <p className="text-muted-foreground text-xs mt-1">Get the URLs from the uploaded images list</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <strong className="text-blue-500">Step 2:</strong> Create JSON with image URLs
                    <p className="text-muted-foreground text-xs mt-1">Use the template and add image_url where needed</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-background rounded-lg">
                    <strong className="text-amber-500">Step 3:</strong> Select exam/subject/chapter
                    <p className="text-muted-foreground text-xs mt-1">Questions will be assigned to selected hierarchy</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <strong className="text-purple-500">Step 4:</strong> Import questions
                    <p className="text-muted-foreground text-xs mt-1">Review preview and click Import</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* AI Parser Tab */}
        {activeTab === 'ai-parser' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Zap className="text-amber-500" size={24} />
                  AI Question Parser & Solution Generator
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Paste question text → AI parses & generates solutions automatically
                </p>
              </div>
            </div>

            {/* Selection Bar */}
            <Card className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <p className="text-sm font-medium mb-3">Select destination:</p>
              <div className="flex gap-4 flex-wrap">
                <select
                  value={selectedExam || ''}
                  onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                >
                  <option value="">Select Exam *</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <select
                  value={selectedSubject || ''}
                  onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedExam}
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  value={selectedChapter || ''}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedSubject}
                >
                  <option value="">Select Chapter</option>
                  {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </Card>

            {/* Input Area */}
            <Card className="p-6 border border-border">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Type className="text-blue-500" size={20} />
                Paste Question Text
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Copy questions directly from PDF, website, or anywhere. AI will parse them automatically.
                Separate multiple questions with blank lines or question numbers (Q1, Q2, 1., 2., etc.)
              </p>
              <textarea
                value={aiParserInput}
                onChange={(e) => setAiParserInput(e.target.value)}
                className="w-full p-4 rounded-xl bg-background border border-border text-sm min-h-[250px] font-mono focus:ring-2 focus:ring-primary"
                placeholder={`Example:

Q28. To measure the temperature coefficient of resistivity α of a semiconductor, an electrical arrangement shown in the figure is prepared. The arm BC is made up of the semiconductor. The experiment is being conducted at 25°C and resistance of the semiconductor arm is 3 mΩ. Arm BC is cooled at a constant rate of 2°C s⁻¹. If the galvanometer G shows no deflection after 10 s, then α is:

(A) 0.01 °C⁻¹
(B) 0.02 °C⁻¹
(C) -0.02 °C⁻¹
(D) -0.01 °C⁻¹

Q29. Another question here...
(A) Option 1
(B) Option 2...`}
              />
              <div className="flex gap-3 mt-4">
                <Button 
                  onClick={handleParseMultipleQuestions} 
                  disabled={aiProcessing || !aiParserInput.trim()}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {aiProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Zap className="mr-2" size={16} />}
                  Parse with AI
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setAiParserInput('')}
                >
                  <X size={16} className="mr-2" /> Clear
                </Button>
              </div>
            </Card>

            {/* Parsed Questions */}
            {aiParsedQuestions.length > 0 && (
              <Card className="p-6 border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-foreground flex items-center gap-2">
                    <Check className="text-green-500" size={20} />
                    Parsed Questions ({aiParsedQuestions.length})
                  </h4>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleGenerateAllSolutions}
                      disabled={aiProcessing}
                      className="text-amber-600 border-amber-500 hover:bg-amber-500/10"
                    >
                      {aiProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Zap className="mr-2" size={16} />}
                      Generate All Solutions
                    </Button>
                    <Button 
                      onClick={handleImportParsedQuestions}
                      disabled={loading || !selectedExam}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      {loading ? <Loader className="animate-spin mr-2" size={16} /> : <Upload className="mr-2" size={16} />}
                      Import All
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {aiParsedQuestions.map((q, i) => (
                    <div key={i} className="p-4 bg-background rounded-xl border border-border">
                      <div className="flex justify-between items-start gap-4 mb-3">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">
                            <span className="text-primary mr-2">Q{i + 1}.</span>
                            {q.question_text}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {q.has_diagram && <Badge className="bg-amber-500/20 text-amber-600 text-xs">Has Diagram</Badge>}
                          <Badge className={`text-xs ${
                            q.difficulty === 'easy' ? 'bg-green-500/20 text-green-600' :
                            q.difficulty === 'hard' ? 'bg-red-500/20 text-red-600' :
                            'bg-yellow-500/20 text-yellow-600'
                          }`}>{q.difficulty || 'medium'}</Badge>
                          {q.correct_answer && (
                            <Badge className="bg-green-500/20 text-green-600 text-xs">Ans: {q.correct_answer}</Badge>
                          )}
                        </div>
                      </div>

                      {/* Options */}
                      {q.options && q.options.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {q.options.map((opt, j) => (
                            <div 
                              key={j} 
                              className={`p-2 rounded-lg text-sm ${
                                opt.label === q.correct_answer 
                                  ? 'bg-green-500/10 border border-green-500/30' 
                                  : 'bg-muted/50'
                              }`}
                            >
                              <span className="font-medium mr-2">{opt.label})</span>
                              <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Diagram Description */}
                      {q.diagram_description && (
                        <div className="p-2 bg-amber-500/10 rounded-lg text-sm mb-3">
                          <strong className="text-amber-600">Diagram:</strong> {q.diagram_description}
                        </div>
                      )}

                      {/* Solution */}
                      {q.explanation && (
                        <div className="p-3 bg-blue-500/5 rounded-lg text-sm border border-blue-500/20">
                          <strong className="text-blue-600 block mb-2">Solution:</strong>
                          <pre className="whitespace-pre-wrap font-sans text-foreground">{q.explanation}</pre>
                        </div>
                      )}

                      {/* Topics */}
                      {q.topics && q.topics.length > 0 && (
                        <div className="flex gap-1 mt-3 flex-wrap">
                          {q.topics.map((topic, j) => (
                            <Badge key={j} variant="outline" className="text-xs">{topic}</Badge>
                          ))}
                        </div>
                      )}

                      {/* Generate solution for single question */}
                      {!q.explanation && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={async () => {
                            const solution = await generateSolutionForQuestion(q);
                            if (solution) {
                              const updated = [...aiParsedQuestions];
                              updated[i] = {
                                ...updated[i],
                                explanation: solution.explanation,
                                correct_answer: solution.correct_answer || updated[i].correct_answer
                              };
                              setAiParsedQuestions(updated);
                            }
                          }}
                          disabled={aiProcessing}
                        >
                          <Zap size={14} className="mr-1" /> Generate Solution
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Instructions */}
            <Card className="p-6 border border-border bg-gradient-to-r from-amber-500/5 to-orange-500/5">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Info className="text-amber-500" size={20} />
                How to Use AI Parser
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-background rounded-lg">
                  <strong className="text-blue-500">1. Copy Text</strong>
                  <p className="text-muted-foreground text-xs mt-1">
                    Select question text from PDF (Ctrl+C) - include options!
                  </p>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <strong className="text-amber-500">2. Parse & Generate</strong>
                  <p className="text-muted-foreground text-xs mt-1">
                    AI parses question structure and generates detailed solutions
                  </p>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <strong className="text-green-500">3. Review & Import</strong>
                  <p className="text-muted-foreground text-xs mt-1">
                    Check parsed questions, then import to database
                  </p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  <strong>Note about images:</strong> AI cannot read images from copy-paste. For questions with diagrams:
                  <br />1. Upload images separately in "Bulk Import" tab
                  <br />2. Add the image URL to the question after parsing
                  <br />3. Or describe the diagram in the text and AI will note it
                </p>
              </div>
            </Card>
          </div>
        )}

          </div>{/* End Page Content */}
        </main>{/* End Main Content Area */}
      </div>{/* End Flex Container */}
    </Layout>
  );
};

export default Admin;
